import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Card, CardContent, CircularProgress, List, ListItem, ListItemText, Avatar, Chip, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar, Checkbox, FormControlLabel, Divider, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend, LineChart, Line } from 'recharts';
import logo from "../../assets/UpdatedMainLogo.png";
import getApiBase from "../../components/getApiBase";
// Session expiration modal removed - too intrusive
// import { useTokenExpiration } from '../hooks/useTokenExpiration';

// Header component
const Header = ({ onLogout, isDeveloper }) => {
  const navigate = useNavigate();
  
  return (
    <header style={{
      backgroundColor: '#fff',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
      padding: '12px 24px',
      position: 'sticky',
      top: 0,
      zIndex: 1000
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <img src={logo} alt="Autoshops.com Logo" style={{ height: 'auto', width: '280px' }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            onClick={() => navigate('/profile')}
            variant="text"
            sx={{
              color: '#ea4335',
              fontWeight: 700,
              fontSize: '18px',
              fontFamily: 'Poppins, sans-serif',
              textTransform: 'none',
              padding: '10px',
              '&:hover': {
                backgroundColor: '#fee2e2'
              }
            }}
          >
            ← Back to Profile
          </Button>
          <Typography sx={{ 
            color: '#000', 
            fontWeight: 700,
            fontSize: '18px',
            fontFamily: 'Poppins, sans-serif'
          }}>
            Admin Dashboard
          </Typography>
          {isDeveloper && (
            <Button
              onClick={() => navigate('/admin/db-management')}
              variant="contained"
              sx={{
                backgroundColor: '#6366f1',
                color: 'white',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: 'Poppins, sans-serif',
                textTransform: 'none',
                boxShadow: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: '#4f46e5',
                  boxShadow: 'none',
                  transform: 'translateY(-1px)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                }
              }}
            >
              🔧 DB Management
            </Button>
          )}
          <Button
            onClick={onLogout}
            variant="contained"
            sx={{
              backgroundColor: '#4285f4',
              color: 'white',
              borderRadius: '6px',
              padding: '6px 16px',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: 'Poppins, sans-serif',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#1d4ed8',
                boxShadow: 'none'
              }
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>
    </header>
  );
};

// Compact Stat Card (Top Row)
const StatCard = ({ title, count, icon, onClick, loading }) => {
  return (
    <Card 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 2
        },
        backgroundColor: '#e5e7eb',
        color: '#111827',
        borderRadius: 2,
        boxShadow: 'none',
        border: '1px solid #d1d5db',
        height: '100%'
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {loading ? (
          <CircularProgress size={20} sx={{ color: '#374151' }} />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 500, display: 'block', mb: 0.25, fontSize: '0.7rem' }}>
                {title}
              </Typography>
              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                fontFamily: 'Poppins, sans-serif'
              }}>
                {count}
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ opacity: 0.6 }}>{icon}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Bugs Tile Card (just like other stat cards)
const BugsTileCard = ({ bugCount, loading, onClick, label = 'Bugs Remaining' }) => {
  return (
    <Card 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 2
        },
        backgroundColor: '#e5e7eb',
        color: '#111827',
        borderRadius: 2,
        boxShadow: 'none',
        border: '1px solid #d1d5db',
        height: '100%'
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {loading ? (
          <CircularProgress size={20} sx={{ color: '#374151' }} />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 500, display: 'block', mb: 0.25, fontSize: '0.7rem' }}>
                {label}
              </Typography>
              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                fontFamily: 'Poppins, sans-serif'
              }}>
                {bugCount}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Recent Activity List
const ActivityCard = ({ activities, onClick }) => {
  return (
    <Card 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 2
        },
        borderRadius: 2,
        boxShadow: 'none',
        border: '1px solid #e5e7eb',
        height: '100%'
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" sx={{ 
            fontWeight: 600, 
            color: '#374151',
            fontFamily: 'Poppins, sans-serif',
            fontSize: '0.8rem'
          }}>
            ⚡ Recent Activity
          </Typography>
        </Box>
        <List sx={{ 
          p: 0, 
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#d1d5db',
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: '#9ca3af'
            }
          }
        }}>
          {activities.map((activity, index) => (
            <ListItem 
              key={index} 
              sx={{ 
                px: 0,
                py: 0.5,
                minHeight: 'auto'
              }}
            >
              <Avatar 
                sx={{ 
                  mr: 1, 
                  width: 28, 
                  height: 28,
                  backgroundColor: activity.color,
                  fontSize: '0.8rem'
                }}
              >
                {activity.icon}
              </Avatar>
              <ListItemText
                primary={
                  <Typography variant="caption" sx={{ fontWeight: 500, color: '#374151', fontSize: '0.75rem' }}>
                    {activity.title}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>
                    {activity.time}
                  </Typography>
                }
                sx={{ my: 0 }}
              />
              <Chip 
                label={activity.status} 
                size="small"
                sx={{ 
                  backgroundColor: activity.statusColor + '15',
                  color: activity.statusColor,
                  fontWeight: 600,
                  fontSize: '0.6rem',
                  height: '18px'
                }}
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

// Area Chart Card - Supports multiple data series
const ChartCard = ({ title, subtitle, data, color, onClick, loading, showUsers = false }) => {
  return (
    <Card 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 2
        },
        borderRadius: 2,
        boxShadow: 'none',
        border: '1px solid #e5e7eb',
        height: '100%'
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ 
            fontWeight: 600, 
            color: '#374151',
            fontFamily: 'Poppins, sans-serif',
            mb: 0.25,
            display: 'block',
            fontSize: '0.8rem'
          }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>
            {subtitle}
          </Typography>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '160px' }}>
            <CircularProgress size={32} sx={{ color }} />
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={data || []}>
              <defs>
                <linearGradient id="grad-shops" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4285f4" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#4285f4" stopOpacity={0.05}/>
                </linearGradient>
                {showUsers && (
                  <linearGradient id="grad-users" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#dc2626" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#dc2626" stopOpacity={0.05}/>
                  </linearGradient>
                )}
              </defs>
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 9 }}
                stroke="#9ca3af"
                angle={-45}
                textAnchor="end"
                height={70}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 10 }}
                stroke="#9ca3af"
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '6px',
                  fontSize: '10px'
                }}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="shops" 
                stroke="#4285f4" 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#grad-shops)"
                name="Autoshops"
              />
              {showUsers && (
                <Area 
                  type="monotone" 
                  dataKey="users" 
                  stroke="#dc2626" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#grad-users)"
                  name="Users"
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Pie Chart Card (Compact)
const PieCard = ({ title, data, colors, onClick, loading }) => {
  return (
    <Card 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 2
        },
        borderRadius: 2,
        boxShadow: 'none',
        border: '1px solid #e5e7eb',
        height: '100%'
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" sx={{ 
          fontWeight: 600, 
          color: '#374151',
          fontFamily: 'Poppins, sans-serif',
          mb: 0.5,
          display: 'block',
          fontSize: '0.8rem'
        }}>
          {title}
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '160px' }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="45%"
                innerRadius={30}
                outerRadius={55}
                fill="#8884d8"
                dataKey="value"
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                labelStyle={{ fontSize: '9px', fontWeight: 600 }}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Legend 
                verticalAlign="bottom" 
                height={30}
                iconSize={8}
                wrapperStyle={{ fontSize: '10px' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '6px',
                  fontSize: '10px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Google Analytics Card
const GoogleAnalyticsCard = ({ onClick, loading }) => {
  return (
    <Card 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 2
        },
        borderRadius: 2,
        boxShadow: 'none',
        border: '1px solid #e5e7eb',
        height: '100%',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#fff' }}>
        <Typography variant="h6" sx={{ 
          fontWeight: 600, 
          fontFamily: 'Poppins, sans-serif',
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          📊 Google Analytics
        </Typography>
        {loading ? (
          <CircularProgress size={40} sx={{ color: '#fff' }} />
        ) : (
          <>
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 2, textAlign: 'center' }}>
              View detailed site traffic and user behavior analytics
            </Typography>
            <Button 
              variant="contained" 
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: '#fff',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              View Dashboard →
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Bug Burndown Chart Card (Simple)
const BugBurndownChart = ({ data, loading, onClick }) => {
  // Transform data for chart - data should be array of { date, displayDate, issues, testing, staging, total }
  const chartData = data && data.length > 0 ? data : [];

  // For the simple view, show only every 7th date to avoid crowding (weekly intervals)
  const displayData = chartData.length > 0 
    ? chartData.filter((_, index) => index % 7 === 0 || index === chartData.length - 1)
    : [];

  return (
    <Card 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 2,
          transform: 'translateY(-2px)'
        },
        borderRadius: 2,
        boxShadow: 'none',
        border: '1px solid #e5e7eb',
        height: '100%'
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ 
            fontWeight: 600, 
            color: '#374151',
            fontFamily: 'Poppins, sans-serif',
            mb: 0.25,
            display: 'block',
            fontSize: '0.8rem'
          }}>
            Bug Burndown Chart
          </Typography>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '160px' }}>
            <CircularProgress size={32} />
          </Box>
        ) : chartData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '160px' }}>
            <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
              No bug data available
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={chartData} animationDuration={2000}>
              <XAxis 
                dataKey="displayDate" 
                tick={{ fontSize: 8 }}
                stroke="#9ca3af"
                angle={-45}
                textAnchor="end"
                height={70}
                interval="preserveStartEnd"
                ticks={displayData.map(d => d.displayDate)}
              />
              <YAxis 
                tick={{ fontSize: 9 }}
                stroke="#9ca3af"
                width={30}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '6px',
                  fontSize: '10px'
                }}
                formatter={(value, name) => {
                  const labels = {
                    issues: 'Development',
                    testing: 'Testing',
                    staging: 'Staging',
                    total: 'Total'
                  };
                  return [`${value}`, labels[name] || name];
                }}
                labelFormatter={(label) => {
                  const dataPoint = chartData.find(d => d.displayDate === label);
                  return dataPoint ? `${dataPoint.dayOfWeek}, ${label}` : label;
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '8px', paddingTop: '5px' }}
                iconType="line"
              />
              <Line
                type="monotone"
                dataKey="issues"
                stroke="#4285f4"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                name="Development"
              />
              <Line
                type="monotone"
                dataKey="testing"
                stroke="#fbbc04"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                name="Testing"
              />
              <Line
                type="monotone"
                dataKey="staging"
                stroke="#6366f1"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                name="Staging"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Circular Progress Card
const CircularCard = ({ title, value, total, color, onClick, loading }) => {
  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
  
  return (
    <Card 
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 2
        },
        borderRadius: 2,
        boxShadow: 'none',
        border: '1px solid #e5e7eb',
        height: '100%'
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="caption" sx={{ 
          fontWeight: 600, 
          color: '#374151',
          fontFamily: 'Poppins, sans-serif',
          mb: 1,
          display: 'block',
          fontSize: '0.8rem'
        }}>
          {title}
        </Typography>
        {loading ? (
          <CircularProgress size={60} sx={{ color }} />
        ) : (
          <>
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                mb: 1
              }}
            >
              <CircularProgress
                variant="determinate"
                value={percentage}
                size={120}
                thickness={6}
                sx={{ color, backgroundColor: '#f3f4f6', borderRadius: '50%' }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}
              >
                <Typography variant="h4" component="div" color="text.primary" sx={{ fontWeight: 700 }}>
                  {percentage}%
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: '#6b7280', display: 'block' }}>
              {value} of {total}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const MasterAdminDashboard = () => {
  const navigate = useNavigate();
  // Session expiration modal removed - too intrusive
  // const { CountdownModal } = useTokenExpiration();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pendingShops: 0,
    totalShops: 0,
    activeShops: 0,
    activeAdmins: 0,
    activePlans: 0,
    shopRegistrationsTotal: 0,
    userRegistrationsTotal: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [shopTrendData, setShopTrendData] = useState([]);
  const [registrationPeriod, setRegistrationPeriod] = useState('');
  
  // QA Tools state
  const [bugsOpen, setBugsOpen] = useState(false);
  const [bugs, setBugs] = useState([]);
  const [bugsLoading, setBugsLoading] = useState(false);
  const [bugsError, setBugsError] = useState('');
  const [bugsSpreadsheetId, setBugsSpreadsheetId] = useState(() => {
    // Load saved spreadsheet ID from localStorage
    return localStorage.getItem('qa_bugs_spreadsheet_id') || '';
  });
  const [bugsSheetName, setBugsSheetName] = useState(() => {
    // Load saved sheet name from localStorage, keyed by spreadsheet ID
    const savedId = localStorage.getItem('qa_bugs_spreadsheet_id') || '';
    if (savedId) {
      const savedSheetName = localStorage.getItem(`qa_bugs_sheet_name_${savedId}`);
      return savedSheetName || 'Sheet1';
    }
    return 'Sheet1';
  });
  const [availableSheets, setAvailableSheets] = useState(() => {
    // Load saved available sheets from localStorage, keyed by spreadsheet ID
    const savedId = localStorage.getItem('qa_bugs_spreadsheet_id') || '';
    if (savedId) {
      try {
        const savedSheets = localStorage.getItem(`qa_bugs_sheets_${savedId}`);
        return savedSheets ? JSON.parse(savedSheets) : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  });
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState({}); // Track which bug is being updated
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState('');
  const [taskWorkflowOpen, setTaskWorkflowOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [isDeveloper, setIsDeveloper] = useState(false);
  const [isQATester, setIsQATester] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false); // Check for admin/stakeholder roles
  const [userRoles, setUserRoles] = useState([]); // Store all user roles
  const [environment, setEnvironment] = useState('dev'); // 'dev', 'test', 'preprod', or 'staging'
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState('');
  const [bugCount, setBugCount] = useState(0);
  const [bugCountLabel, setBugCountLabel] = useState('Bugs Remaining');
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [qaEmailManagementOpen, setQaEmailManagementOpen] = useState(false);
  const [customEmailsToReset, setCustomEmailsToReset] = useState('');
  const [checkingDependencies, setCheckingDependencies] = useState(false);
  const [dependencies, setDependencies] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [managedEmails, setManagedEmails] = useState([]);
  const [loadingManagedEmails, setLoadingManagedEmails] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);
  const [zipcode, setZipcode] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [refreshReviewsOpen, setRefreshReviewsOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [refreshDays, setRefreshDays] = useState('7');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editingDevComment, setEditingDevComment] = useState('');
  const [savingDevComment, setSavingDevComment] = useState(false);
  const [bugsSheetGid, setBugsSheetGid] = useState('');
  const [burndownData, setBurndownData] = useState([]);
  const [burndownLoading, setBurndownLoading] = useState(false);
  const [burndownModalOpen, setBurndownModalOpen] = useState(false);

  // Fetch burndown data (backend automatically initializes history if needed)
  const fetchBurndownData = async () => {
    if (!bugsSpreadsheetId) {
      setBurndownData([]);
      return;
    }

    try {
      setBurndownLoading(true);
      const response = await fetch(
        `${getApiBase()}/api/qa/bugs/burndown?spreadsheetId=${encodeURIComponent(bugsSpreadsheetId)}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setBurndownData(data.data || []);
      } else {
        setBurndownData([]);
      }
    } catch (error) {
      console.error('Error fetching burndown data:', error);
      setBurndownData([]);
    } finally {
      setBurndownLoading(false);
    }
  };


  // Fetch burndown data when spreadsheet ID changes
  useEffect(() => {
    fetchBurndownData();
  }, [bugsSpreadsheetId]);

  useEffect(() => {
    checkAuth();
    fetchStats();
    
    // Detect environment from API base URL
    const detectEnvironment = () => {
      const apiBase = getApiBase();
      // Check environment variable first (most reliable)
      if (process.env.REACT_APP_ENVIRONMENT) {
        const env = process.env.REACT_APP_ENVIRONMENT.toLowerCase();
        setEnvironment(env);
        return;
      }
      // Fallback: Check if API URL contains environment indicators
      if (apiBase.includes('test') || apiBase.includes('qa')) {
        setEnvironment('test');
      } else if (apiBase.includes('preprod') || apiBase.includes('staging')) {
        setEnvironment('preprod');
      } else {
        setEnvironment('dev');
      }
    };
    
    detectEnvironment();
    
    // Check user roles (Developer, QA, Tester)
    const checkUserRoles = async () => {
      try {
        // Get current user info first
        const userResponse = await fetch(`${getApiBase()}/api/auth/me`, {
          credentials: 'include'
        });
        if (!userResponse.ok) {
          setIsDeveloper(false);
          setIsQATester(false);
          setUserRoles([]);
          return;
        }
        const userData = await userResponse.json();
        const currentUserEmail = userData.user?.email;
        setUserEmail(currentUserEmail || '');
        
        if (!currentUserEmail) {
          setIsDeveloper(false);
          setIsQATester(false);
          setUserRoles([]);
          return;
        }
        
        // Get all admins to find current user's roles
        const response = await fetch(`${getApiBase()}/api/admin-management/roles`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          // Find current user in the admins list
          const currentUser = data.admins?.find((admin) => admin.email === currentUserEmail);
          if (currentUser && currentUser.admin_roles) {
            const activeRoles = currentUser.admin_roles
              .filter((role) => role.is_active !== false)
              .map((role) => role.role_type);
            
            setUserRoles(activeRoles);
            
            // Check for developer role (super_admin, admin, developer)
            // Also check if developer is in primary OR secondary role
            const developerRoles = ['super_admin', 'admin', 'developer'];
            const hasDeveloperRole = activeRoles.some((role) => 
              developerRoles.includes(role)
            );
            
            // Check if Developer is in primary OR secondary role
            const hasDeveloperInPrimary = currentUser.admin_roles.some(
              (role) => role.role_type === 'developer' && role.primary_role === true && role.is_active !== false
            );
            const hasDeveloperInSecondary = currentUser.admin_roles.some(
              (role) => role.role_type === 'developer' && (role.primary_role === false || !role.primary_role) && role.is_active !== false
            );
            
            // Set isDeveloper if they have developer role in primary, secondary, or have super_admin/admin
            setIsDeveloper(hasDeveloperRole || hasDeveloperInPrimary || hasDeveloperInSecondary);
            
            // Check for QA/Tester role
            const qaRoles = ['qa', 'tester'];
            const hasQARole = activeRoles.some((role) => 
              qaRoles.includes(role)
            );
            setIsQATester(hasQARole);
            
            // Check for Admin/Stakeholder role (super_admin, admin) - primary or secondary
            const adminRoles = ['super_admin', 'admin'];
            const hasAdminRole = activeRoles.some((role) => 
              adminRoles.includes(role)
            );
            // Also check if admin role is in primary OR secondary
            const hasAdminInPrimary = currentUser.admin_roles.some(
              (role) => adminRoles.includes(role.role_type) && role.primary_role === true && role.is_active !== false
            );
            const hasAdminInSecondary = currentUser.admin_roles.some(
              (role) => adminRoles.includes(role.role_type) && (role.primary_role === false || !role.primary_role) && role.is_active !== false
            );
            setIsAdmin(hasAdminRole || hasAdminInPrimary || hasAdminInSecondary);
            
            return;
          }
        }
        setIsDeveloper(false);
        setIsQATester(false);
        setIsAdmin(false);
        setUserRoles([]);
      } catch (error) {
        console.error('Error checking user roles:', error);
        setIsDeveloper(false);
        setIsQATester(false);
        setIsAdmin(false);
        setUserRoles([]);
      }
    };
    
    checkUserRoles();
  }, []);

  // Initialize dev comment when task changes
  useEffect(() => {
    if (tasks.length > 0 && currentTaskIndex >= 0 && currentTaskIndex < tasks.length) {
      const task = tasks[currentTaskIndex];
      // Check for dev_comment or Dev Comment column (case insensitive)
      const devCommentKey = Object.keys(task).find(k => 
        k.toLowerCase().replace(/\s+/g, '_') === 'dev_comment' || 
        k.toLowerCase() === 'dev comment'
      );
      setEditingDevComment(devCommentKey ? (task[devCommentKey] || '') : '');
    }
  }, [currentTaskIndex, tasks]);

  // Fetch and store the sheet GID when sheet name changes (with caching to prevent quota issues)
  useEffect(() => {
    const fetchSheetGid = async () => {
      // Reset GID when sheet name or spreadsheet changes
      setBugsSheetGid('');
      
      if (bugsSpreadsheetId && bugsSheetName) {
        // Check cache first (5 minute cache)
        const cacheKey = `sheet_gid_${bugsSpreadsheetId}_${bugsSheetName}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          try {
            const { gid, timestamp } = JSON.parse(cached);
            const age = Date.now() - timestamp;
            if (age < 5 * 60 * 1000) { // 5 minutes
              console.log('✅ Using cached GID:', gid);
              setBugsSheetGid(gid);
              return;
            }
          } catch (e) {
            // Invalid cache, continue to fetch
          }
        }
        
        console.log('🔍 Fetching GID for:', { bugsSpreadsheetId, bugsSheetName });
        try {
          const response = await fetch(
            `${getApiBase()}/api/qa/bugs/metadata?spreadsheetId=${bugsSpreadsheetId}`,
            { credentials: 'include' }
          );
          const data = await response.json();
          console.log('📋 Metadata response:', data);
          
          // Handle quota errors
          if (response.status === 429 || (data.error && data.error.includes('quota'))) {
            console.warn('⚠️ Quota exceeded, using cached data if available');
            return;
          }
          
          if (data.success && data.metadata && data.metadata.sheets) {
            console.log('📊 Available sheets:', data.metadata.sheets.map(s => ({ title: s.title, id: s.id })));
            
            // Find the sheet by name (case-insensitive match)
            const sheet = data.metadata.sheets.find(s => 
              s.title === bugsSheetName || s.title.toLowerCase() === bugsSheetName.toLowerCase()
            );
            console.log('🎯 Found sheet:', sheet);
            console.log('🔍 Searching for sheet name:', bugsSheetName);
            
            if (sheet && sheet.id !== undefined && sheet.id !== null) {
              const gid = sheet.id.toString();
              setBugsSheetGid(gid);
              console.log(`✅ Set GID for "${bugsSheetName}": ${gid}`);
              
              // Cache the result
              localStorage.setItem(cacheKey, JSON.stringify({ gid, timestamp: Date.now() }));
            } else {
              console.log('❌ Sheet not found or missing ID');
              console.log('Available sheet titles:', data.metadata.sheets.map(s => s.title));
              setBugsSheetGid(''); // Reset if not found
            }
          } else {
            console.log('❌ Invalid metadata response structure:', data);
            setBugsSheetGid(''); // Reset on invalid response
          }
        } catch (error) {
          console.error('❌ Error fetching sheet GID:', error);
          setBugsSheetGid(''); // Reset on error
        }
      } else {
        console.log('⚠️ Missing spreadsheetId or sheetName:', { bugsSpreadsheetId, bugsSheetName });
        setBugsSheetGid(''); // Reset if missing
      }
    };
    fetchSheetGid();
  }, [bugsSpreadsheetId, bugsSheetName]);

  // Log link details when they change (for debugging)
  useEffect(() => {
    if (tasks[currentTaskIndex] && tasks[currentTaskIndex].id && bugsSpreadsheetId) {
      const currentTaskId = tasks[currentTaskIndex].id || tasks[currentTaskIndex].bug_id || tasks[currentTaskIndex]['#'] || currentTaskIndex + 1;
      const rowNumber = parseInt(currentTaskId) + 1;
      const linkUrl = bugsSheetGid 
        ? `https://docs.google.com/spreadsheets/d/${bugsSpreadsheetId}/edit?gid=${bugsSheetGid}#range=L${rowNumber}`
        : `https://docs.google.com/spreadsheets/d/${bugsSpreadsheetId}/edit`;
      
      console.log('🔗 Building link with:', { 
        bugsSpreadsheetId, 
        bugsSheetGid, 
        bugsSheetName,
        currentTaskId: currentTaskId,
        rowNumber: rowNumber,
        linkUrl: linkUrl
      });
    }
  }, [tasks, currentTaskIndex, bugsSpreadsheetId, bugsSheetGid, bugsSheetName]);

  // Helper function to filter tasks based on environment and role
  const filterTasksByEnvironmentAndRole = (tasksList) => {
    if (!tasksList || tasksList.length === 0) return [];
    
    // Find status column
    const statusKey = tasksList.length > 0 
      ? Object.keys(tasksList[0] || {}).find(k => k.toLowerCase().includes('status'))
      : null;
    
    if (!statusKey) return tasksList; // If no status column, return all
    
    return tasksList.filter((task) => {
      const status = (task[statusKey] || '').toString().trim();
      
      // DEV Environment - Developer
      if (environment === 'dev' && isDeveloper) {
        const allowedStatuses = ['New', 'In Progress', 'Fixed (Dev)', 'Failed in Test', 'Failed in Staging', 'Hold'];
        return allowedStatuses.includes(status);
      }
      
      // TEST Environment - QA/Tester
      // Can see: Deployed to Test, Deployed to Prod, Closed (to verify closures), Hold
      if (environment === 'test' && isQATester) {
        const allowedStatuses = ['Deployed to Test', 'Deployed to Prod', 'Closed', 'Hold'];
        return allowedStatuses.includes(status);
      }
      
      // TEST Environment - Developer
      // Developers can see: Verified in Test, Ready for Staging, Hold
      // "Ready for Staging" is still in TEST environment until deployed to staging
      // They CANNOT see "Deployed to Staging" - those bugs have moved to PREPROD
      // They CANNOT see "Deployed to Prod" - that's for PREPROD environment
      if (environment === 'test' && isDeveloper) {
        const allowedStatuses = ['Verified in Test', 'Ready for Staging', 'Hold'];
        return allowedStatuses.includes(status);
      }
      
      // PREPROD/STAGING Environment - QA Tester
      // Can see: Deployed to Staging, Deployed to Prod, Closed, Hold
      if (environment === 'preprod' && isQATester) {
        const allowedStatuses = ['Deployed to Staging', 'Deployed to Prod', 'Closed', 'Hold'];
        return allowedStatuses.includes(status);
      }
      
      // PREPROD/STAGING Environment - Developer
      // Developers can see: Verified in Staging (after QA verifies), Hold
      // They can deploy from Verified in Test to Deployed to Staging (but that's in Test env)
      if (environment === 'preprod' && isDeveloper) {
        const allowedStatuses = ['Verified in Staging', 'Hold'];
        return allowedStatuses.includes(status);
      }
      
      // PREPROD/STAGING Environment - Admin/Stakeholder (any admin role - primary or secondary)
      // Can see: Verified in Staging, Ready for Production, Deployed to Prod, Hold
      // NOTE: "Ready for Staging" is in TEST environment, not STAGING
      // Stakeholders can approve features for production
      if (environment === 'preprod' && isAdmin) {
        const allowedStatuses = ['Verified in Staging', 'Ready for Production', 'Deployed to Prod', 'Hold'];
        return allowedStatuses.includes(status);
      }
      
      // Default: show nothing if no match
      return false;
    });
  };

  // Helper function to get allowed status transitions based on current status, environment, and role
  const getAllowedStatusTransitions = (currentStatus) => {
    const status = (currentStatus || '').toString().trim();
    
    // Cannot transition from Closed status
    if (status === 'Closed') {
      return [];
    }
    
    // Base transitions for each status (without Hold)
    let baseTransitions = [];
    
    // DEV Environment - Developer transitions
    if (environment === 'dev' && isDeveloper) {
      if (status === 'New') {
        baseTransitions = ['In Progress'];
      } else if (status === 'In Progress') {
        baseTransitions = ['New', 'Fixed (Dev)'];
      } else if (status === 'Fixed (Dev)') {
        baseTransitions = ['In Progress', 'Deployed to Test'];
      } else if (status === 'Failed in Test') {
        baseTransitions = ['In Progress'];
      } else if (status === 'Failed in Staging') {
        baseTransitions = ['In Progress'];
      } else if (status === 'Hold') {
        // From Hold, can resume to any status visible in Dev
        return ['New', 'In Progress', 'Fixed (Dev)'];
      }
    }
    
    // TEST Environment - QA/Tester transitions
    if (environment === 'test' && isQATester) {
      if (status === 'Deployed to Test') {
        baseTransitions = ['Failed in Test', 'Verified in Test'];
      } else if (status === 'Deployed to Prod') {
        // QA/Tester closes bugs that are production-ready
        baseTransitions = ['Closed'];
      } else if (status === 'Hold') {
        // From Hold, can resume to any status visible in Test for QA
        return ['Deployed to Test', 'Deployed to Prod'];
      }
    }
    
      // TEST Environment - Developer transitions
    if (environment === 'test' && isDeveloper) {
      if (status === 'Verified in Test') {
        // Developer can move Verified in Test to Ready for Staging (still in test until deployed)
        baseTransitions = ['Ready for Staging', 'Hold'];
      } else if (status === 'Ready for Staging') {
        // Developer can deploy from Ready for Staging to Deployed to Staging (moves to PREPROD)
        baseTransitions = ['Deployed to Staging', 'Hold'];
      } else if (status === 'Hold') {
        // From Hold, can resume to Verified in Test or Ready for Staging
        return ['Verified in Test', 'Ready for Staging'];
      }
    }
    
    // PREPROD/STAGING Environment - QA Tester transitions
    if (environment === 'preprod' && isQATester) {
      if (status === 'Deployed to Staging') {
        baseTransitions = ['Verified in Staging', 'Failed in Staging'];
      } else if (status === 'Deployed to Prod') {
        // QA/Tester closes bugs that are production-ready
        baseTransitions = ['Closed'];
      } else if (status === 'Hold') {
        // From Hold, can resume to Deployed to Staging
        return ['Deployed to Staging'];
      }
    }
    
    // PREPROD/STAGING Environment - Developer transitions
    // Developers can see Verified in Staging (after QA verifies) but typically don't need to act on it
    if (environment === 'preprod' && isDeveloper) {
      if (status === 'Verified in Staging') {
        // Verified in Staging moves to Ready for Production (Admin action)
        // Developer typically doesn't need to do anything, but can put on hold if needed
        baseTransitions = ['Hold'];
      } else if (status === 'Hold') {
        return ['Verified in Staging'];
      }
    }
    
    // PREPROD/STAGING Environment - Admin/Stakeholder transitions (any admin role)
    // Stakeholders can approve features: Verified in Staging → Ready for Production
    // NOTE: "Ready for Staging" is in TEST environment, not STAGING
    // Automation will move Ready for Production → Deployed to Prod on release day
    if (environment === 'preprod' && isAdmin) {
      if (status === 'Verified in Staging') {
        // Admin can move Verified in Staging to Ready for Production (after staging validation)
        baseTransitions = ['Ready for Production'];
      } else if (status === 'Deployed to Staging') {
        // Admin can also see Deployed to Staging (for oversight)
        baseTransitions = ['Ready for Production'];
      } else if (status === 'Ready for Production') {
        // Can move back to staging if needed, or automation will move to Deployed to Prod
        baseTransitions = ['Deployed to Staging'];
      } else if (status === 'Deployed to Prod') {
        // In PREPROD, can close bugs that are production-ready
        baseTransitions = ['Closed'];
      } else if (status === 'Hold') {
        // From Hold, can resume to Verified in Staging or Deployed to Staging
        return ['Verified in Staging', 'Deployed to Staging'];
      }
    }
    
    // Add Hold to transitions if not in Prod environment and status is not Closed
    // All roles (Developer, QA, Tester) can move to Hold in Dev, Test, and Preprod
    if (environment !== 'prod' && status !== 'Hold' && status !== 'Closed') {
      baseTransitions.push('Hold');
    }
    
    return baseTransitions;
  };

  const checkAuth = async () => {
    try {
      const response = await fetch(`${getApiBase()}/api/auth/me`, {
        credentials: "include",
      });

      if (!response.ok) {
        navigate("/admin");
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      navigate("/admin");
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch pending claims count from Mechanic_Autoshops
      const pendingClaimsResponse = await fetch(`${getApiBase()}/api/admin/pending-claims-count`, {
        credentials: 'include'
      });
      if (pendingClaimsResponse.ok) {
        const pendingData = await pendingClaimsResponse.json();
        setStats(prev => ({ 
          ...prev, 
          pendingShops: pendingData.count || 0
        }));
      }
      
      const shopsResponse = await fetch(`${getApiBase()}/api/admin/all-shops`, {
        credentials: 'include'
      });
      if (shopsResponse.ok) {
        const shops = await shopsResponse.json();
        const active = shops.filter(s => s.status === 'Active').length;
        setStats(prev => ({ 
          ...prev, 
          totalShops: shops.length,
          activeShops: active
        }));
      }

      const adminsResponse = await fetch(`${getApiBase()}/api/admin-management/roles`, {
        credentials: 'include'
      });
      if (adminsResponse.ok) {
        const data = await adminsResponse.json();
        const adminsList = data.admins || [];
        // Count unique users (endpoint already filters for active roles only)
        setStats(prev => ({ ...prev, activeAdmins: adminsList.length }));
      }

      // Count active plan definitions (not subscriptions) - matches pricing dashboard logic
      const plansResponse = await fetch(`${getApiBase()}/api/admin/subscription-plans`, {
        credentials: 'include'
      });
      if (plansResponse.ok) {
        const data = await plansResponse.json();
        const plans = data.plans || [];
        const activePlansCount = plans.filter(p => p.is_active === 1).length;
        setStats(prev => ({ ...prev, activePlans: activePlansCount }));
      } else {
        // Fallback to 0 if endpoint doesn't exist yet
        setStats(prev => ({ ...prev, activePlans: 0 }));
      }

      // Fetch bug count from ALL tabs if spreadsheet is configured
      const savedSpreadsheetId = localStorage.getItem('qa_bugs_spreadsheet_id');
      
      if (savedSpreadsheetId) {
        try {
          // Detect environment from API base URL (same logic as detectEnvironment)
          const apiBase = getApiBase();
          let env = 'dev'; // default
          if (apiBase.includes('test') || apiBase.includes('qa')) {
            env = 'test';
          } else if (apiBase.includes('preprod') || apiBase.includes('staging')) {
            env = 'preprod';
          }
          // Override with environment variable if available
          if (process.env.REACT_APP_ENVIRONMENT) {
            env = process.env.REACT_APP_ENVIRONMENT.toLowerCase();
          }
          
          const countResponse = await fetch(
            `${getApiBase()}/api/qa/bugs/count?spreadsheetId=${encodeURIComponent(savedSpreadsheetId)}&environment=${encodeURIComponent(env)}`,
            { credentials: 'include' }
          );
          if (countResponse.ok) {
            const countData = await countResponse.json();
            setBugCount(countData.count || 0);
            // Update label from backend response
            if (countData.label) {
              setBugCountLabel(countData.label);
            }
          }
        } catch (error) {
          console.error('Error fetching bug count:', error);
          // Don't show error - just leave count at 0
        }
      }

      // Fetch recent activity
      const activityResponse = await fetch(`${getApiBase()}/api/admin/recent-activity`, {
        credentials: 'include'
      });
      if (activityResponse.ok) {
        const activities = await activityResponse.json();
        setRecentActivities(activities);
      }

      // Fetch registration trend (previous month + current month)
      const trendResponse = await fetch(`${getApiBase()}/api/admin/shop-registrations-trend`, {
        credentials: 'include'
      });
      if (trendResponse.ok) {
        const trendData = await trendResponse.json();
        console.log('📊 Registration trend data received:', trendData);
        const trendArray = (trendData.trend || []).map(item => {
          // Convert date string to local time for display
          // Backend sends date in YYYY-MM-DD format (UTC), convert to local date
          const date = new Date(item.date + 'T00:00:00'); // Parse as local time
          const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const dayName = dayNames[date.getDay()];
          const dayOfMonth = date.getDate();
          
          return {
            ...item,
            name: `${dayName} ${dayOfMonth}` // Regenerate name in local timezone
          };
        });
        console.log('📊 Trend array length:', trendArray.length);
        if (trendArray.length > 0) {
          console.log('📊 First few data points:', trendArray.slice(0, 3));
        }
        setShopTrendData(trendArray);
        setRegistrationPeriod(trendData.period || '');
        // Store totals for display
        if (trendData.shopsTotal !== undefined) {
          setStats(prev => ({ ...prev, shopRegistrationsTotal: trendData.shopsTotal }));
        }
        if (trendData.usersTotal !== undefined) {
          setStats(prev => ({ ...prev, userRegistrationsTotal: trendData.usersTotal }));
        }
      } else {
        const errorData = await trendResponse.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ Failed to fetch registration trend:', trendResponse.status, errorData);
      }

    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${getApiBase()}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      // Clear all auth-related localStorage
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      
      // Determine the correct base domain for the current environment
      const hostname = window.location.hostname;
      let baseDomain;
      
      if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
        baseDomain = `http://localhost:${window.location.port || 3000}`;
      } else if (hostname.includes('vercel.app')) {
        // Vercel preview deployments - stay on the same preview URL
        baseDomain = `${window.location.protocol}//${hostname}`;
      } else if (hostname.includes('test.autoshops.com')) {
        baseDomain = 'https://test.autoshops.com';
      } else if (hostname.includes('preprod.autoshops.com')) {
        baseDomain = 'https://preprod.autoshops.com';
      } else {
        baseDomain = 'https://autoshops.com';
      }
      
      // Use window.location.href to force a full page reload and clear all state
      window.location.href = `${baseDomain}/login`;
    } catch (err) {
      console.error("Logout failed:", err);
      // Clear localStorage even on error
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      
      // Determine the correct base domain for the current environment
      const hostname = window.location.hostname;
      let baseDomain;
      
      if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
        baseDomain = `http://localhost:${window.location.port || 3000}`;
      } else if (hostname.includes('vercel.app')) {
        baseDomain = `${window.location.protocol}//${hostname}`;
      } else if (hostname.includes('test.autoshops.com')) {
        baseDomain = 'https://test.autoshops.com';
      } else if (hostname.includes('preprod.autoshops.com')) {
        baseDomain = 'https://preprod.autoshops.com';
      } else {
        baseDomain = 'https://autoshops.com';
      }
      
      // Force full page reload even on error
      window.location.href = `${baseDomain}/login`;
    }
  };

  const handleCheckDependencies = async () => {
    setCheckingDependencies(true);
    try {
      const response = await fetch(`${getApiBase()}/api/qa/test-accounts/dependencies`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        setDependencies(data);
      } else {
        let errorMessage = 'Failed to check dependencies';
        try {
          const errorData = await response.json();
          
          // Check if it's a permission/role error and show friendly message
          if (response.status === 403 && (errorData.error?.includes('Permission denied') || errorData.error?.includes('access required'))) {
            errorMessage = environment === 'test'
              ? 'Only Testers may manage the QA test emails.'
              : 'Only Developers may manage the QA test emails.';
          } else {
            errorMessage = errorData.error || errorMessage;
            if (errorData.yourRoles) {
              errorMessage += ` (Your roles: ${errorData.yourRoles})`;
            }
          }
        } catch (e) {
          // If response is not JSON, use status text
          if (response.status === 403) {
            errorMessage = environment === 'test'
              ? 'Only Testers may manage the QA test emails.'
              : 'Only Developers may manage the QA test emails.';
          } else {
            errorMessage = `Error ${response.status}: ${response.statusText || 'Unknown error'}`;
          }
        }
        console.error('Check dependencies error:', {
          status: response.status,
          statusText: response.statusText,
          url: `${getApiBase()}/api/qa/test-accounts/dependencies`
        });
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: response.status === 403 ? 'info' : 'error' // Changed from 'error' to 'info' for friendlier UX
        });
      }
    } catch (error) {
      console.error('Error checking dependencies:', error);
      setSnackbar({
        open: true,
        message: `Network error: ${error.message || 'Please try again.'}`,
        severity: 'error'
      });
    } finally {
      setCheckingDependencies(false);
    }
  };

  // Fetch managed emails for the current user
  const fetchManagedEmails = async () => {
    if (!userEmail || (environment !== 'test' && environment !== 'dev' && environment !== 'preprod')) {
      setManagedEmails([]);
      return;
    }
    
    setLoadingManagedEmails(true);
    try {
      console.log('🔍 Fetching managed emails:', { userEmail, environment });
      const response = await fetch(`${getApiBase()}/api/admin-management/email-relationships?environment=${environment}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📧 API response:', data);
        console.log('📧 All relationships:', data.relationships);
        // Filter to only show emails managed by the current user
        const userManagedEmails = (data.relationships || [])
          .filter((rel) => {
            const matches = rel.admin_email?.toLowerCase() === userEmail.toLowerCase() && rel.is_active;
            console.log('🔍 Checking relationship:', { 
              relAdminEmail: rel.admin_email, 
              userEmail, 
              matches,
              isActive: rel.is_active 
            });
            return matches;
          })
          .map((rel) => rel.managed_email?.toLowerCase().trim())
          .filter((email) => email);
        
        console.log('✅ Filtered managed emails:', userManagedEmails);
        setManagedEmails(userManagedEmails);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.warn('❌ Failed to fetch managed emails:', response.status, errorData);
        setManagedEmails([]);
      }
    } catch (error) {
      console.error('Error fetching managed emails:', error);
      setManagedEmails([]);
    } finally {
      setLoadingManagedEmails(false);
    }
  };

  // Fetch managed emails when dialog opens
  useEffect(() => {
    if (qaEmailManagementOpen && userEmail && (environment === 'test' || environment === 'dev' || environment === 'preprod')) {
      fetchManagedEmails();
    }
  }, [qaEmailManagementOpen, userEmail, environment]);

  const handleResetTestAccounts = async (emails = null) => {
    const confirmMessage = emails && emails.length > 0
      ? `Are you sure you want to reset ${emails.length} selected test account(s)? This will delete all test data and cannot be undone.`
      : 'Are you sure you want to reset all test accounts? This will delete all test data and cannot be undone.';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setResetting(true);
    try {
      const body = emails && emails.length > 0 ? { emails } : {};
      const response = await fetch(`${getApiBase()}/api/qa/test-accounts/reset`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        setSnackbar({
          open: true,
          message: data.message || 'Test accounts reset successfully',
          severity: 'success'
        });
        setDependencies(null);
        setSelectedEmails(new Set());
        setCustomEmailsToReset(''); // Clear custom email field
        // Refresh managed emails list
        if (userEmail && (environment === 'test' || environment === 'dev' || environment === 'preprod')) {
          fetchManagedEmails();
        }
        // Refresh stats
        fetchStats();
      } else {
        const errorData = await response.json();
        
        // Check if it's a permission/role error and show friendly message
        let errorMessage = errorData.error || 'Failed to reset test accounts';
        if (response.status === 403 && (errorData.error?.includes('Permission denied') || errorData.error?.includes('access required'))) {
          errorMessage = environment === 'test'
            ? 'Only Testers may manage the QA test emails.'
            : 'Only Developers may manage the QA test emails.';
        }
        
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: response.status === 403 ? 'info' : 'error' // Friendly message for permission errors
        });
      }
    } catch (error) {
      console.error('Error resetting test accounts:', error);
      setSnackbar({
        open: true,
        message: 'Network error. Please try again.',
        severity: 'error'
      });
    } finally {
      setResetting(false);
    }
  };

  const handleBulkImport = async () => {
    if (!zipcode || zipcode.length !== 5) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid 5-digit ZIP code',
        severity: 'error'
      });
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const response = await fetch(`${getApiBase()}/api/qa/bulk-import-shops`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ zipcode })
      });

      const data = await response.json();

      if (response.ok) {
        setImportResult({
          success: true,
          summary: data.summary
        });
        setSnackbar({
          open: true,
          message: `Successfully imported ${data.summary.created} shops from ${zipcode}`,
          severity: 'success'
        });
        // Refresh stats
        fetchStats();
      } else {
        setImportResult({
          success: false,
          error: data.error || 'Import failed'
        });
        setSnackbar({
          open: true,
          message: data.error || 'Import failed',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Bulk import error:', error);
      setImportResult({
        success: false,
        error: 'Failed to connect to server'
      });
      setSnackbar({
        open: true,
        message: 'Failed to connect to server',
        severity: 'error'
      });
    } finally {
      setImporting(false);
    }
  };

  const handleRefreshReviews = async () => {
    if (!refreshDays || parseInt(refreshDays) < 1) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid number of days',
        severity: 'error'
      });
      return;
    }

    setRefreshing(true);
    setRefreshResult(null);

    try {
      const response = await fetch(`${getApiBase()}/api/admin/refresh-reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ recent_days: parseInt(refreshDays) })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setRefreshResult(data);
        setSnackbar({
          open: true,
          message: `Successfully refreshed reviews for ${data.summary.success} out of ${data.summary.total} shops`,
          severity: 'success'
        });
      } else {
        setRefreshResult({ success: false, error: data.error || data.message });
        setSnackbar({
          open: true,
          message: data.error || data.message || 'Failed to refresh reviews',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Refresh reviews error:', error);
      setRefreshResult({ success: false, error: error.message });
      setSnackbar({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setRefreshing(false);
    }
  };

  const shopStatusData = [
    { name: 'Pending', value: stats.pendingShops },
    { name: 'Active', value: stats.activeShops },
    { name: 'Other', value: Math.max(0, stats.totalShops - stats.pendingShops - stats.activeShops) }
  ];

  const pieColors = ['#f59e0b', '#059669', '#9ca3af'];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <Header onLogout={handleLogout} isDeveloper={isDeveloper} />
      
      <Box sx={{ p: 3, maxWidth: '1600px', mx: 'auto' }}>
        {/* Top Stats - 5 Cards (300x100px each) */}
        <Box sx={{ 
          display: 'flex',
          gap: 2,
          mb: 2
        }}>
          <Box sx={{ width: '300px', height: '100px' }}>
            <StatCard
              title="Total Shops"
              count={stats.totalShops}
              icon="🏪"
              onClick={() => navigate('/admin/shops')}
              loading={loading}
            />
          </Box>
          <Box sx={{ width: '300px', height: '100px' }}>
            <StatCard
              title="Pending Review"
              count={stats.pendingShops}
              icon="⏳"
              onClick={() => navigate('/admin/shops')}
              loading={loading}
            />
          </Box>
          <Box sx={{ width: '300px', height: '100px' }}>
            <StatCard
              title="Active Plans"
              count={stats.activePlans}
              icon="💰"
              onClick={() => navigate('/admin/pricing-dashboard')}
              loading={loading}
            />
          </Box>
          <Box sx={{ width: '300px', height: '100px' }}>
            <StatCard
              title="Team Members"
              count={stats.activeAdmins}
              icon="👥"
              onClick={() => navigate('/admin/management')}
              loading={loading}
            />
          </Box>
          <Box sx={{ width: '300px', height: '100px' }}>
            <BugsTileCard
              bugCount={bugCount}
              label={bugCountLabel}
              loading={loading}
              onClick={() => setBugsOpen(true)}
            />
          </Box>
        </Box>

        {/* Main Grid - Perfect edge alignment */}
        <Box sx={{ 
          display: 'flex',
          gap: 2
        }}>
          {/* Left column - Recent Activity + Google Analytics */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Recent Activity - 300×200px */}
            <Box sx={{ width: '300px', height: '200px' }}>
              <ActivityCard
                activities={recentActivities}
                onClick={() => {}}
              />
            </Box>
            
            {/* Google Analytics - 300×200px */}
            <Box sx={{ width: '300px', height: '200px' }}>
              <GoogleAnalyticsCard
                onClick={() => window.open('https://analytics.google.com', '_blank')}
                loading={false}
              />
            </Box>
          </Box>

          {/* Middle column - Charts */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Shop Registrations - 932×200px */}
            <Box sx={{ width: '932px', height: '200px' }}>
              <ChartCard
                title="Total Active Registrations"
                subtitle={registrationPeriod || 'Previous month + Current month'}
                data={shopTrendData}
                color="#4285f4"
                onClick={() => navigate('/admin/shops?status=Active')}
                loading={loading}
                showUsers={true}
              />
            </Box>

            {/* Bottom row - Shop Status + Approval Rate + Bug Burndown (combined = 932px) */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ width: '300px', height: '200px' }}>
                <PieCard
                  title="Shop Status"
                  data={shopStatusData}
                  colors={pieColors}
                  onClick={() => navigate('/admin/shops?status=All')}
                  loading={loading}
                />
              </Box>
              <Box sx={{ width: '300px', height: '200px' }}>
                <CircularCard
                  title="Approval Rate"
                  value={stats.activeShops}
                  total={stats.activeShops + stats.pendingShops}
                  color="#059669"
                  onClick={() => navigate('/admin/shops')}
                  loading={loading}
                />
              </Box>
              <Box sx={{ width: '300px', height: '200px' }}>
                <BugBurndownChart
                  data={burndownData}
                  loading={burndownLoading}
                  onClick={() => setBurndownModalOpen(true)}
                />
              </Box>
            </Box>
          </Box>

          {/* Far Right column - QA Tools Buttons aligned with Bugs tile */}
          <Box sx={{ 
            width: '300px',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}>
            <Button
              variant="contained"
              onClick={() => setBulkImportOpen(true)}
              fullWidth
              sx={{
                backgroundColor: '#2563eb',
                color: 'white',
                borderRadius: '6px',
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: 'Poppins, sans-serif',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#1d4ed8'
                }
              }}
            >
              Bulk Import Shops
            </Button>
            <Button
              variant="contained"
              onClick={() => setRefreshReviewsOpen(true)}
              fullWidth
              sx={{
                backgroundColor: '#10b981',
                color: 'white',
                borderRadius: '6px',
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: 'Poppins, sans-serif',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#059669'
                }
              }}
            >
              Refresh Reviews
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                // Check if user has access based on environment
                const hasAccess = environment === 'test' 
                  ? isQATester  // TEST: requires tester or qa role
                  : isDeveloper; // DEV: requires developer role
                
                if (hasAccess) {
                  setQaEmailManagementOpen(true);
                } else {
                  // Show friendly message based on environment
                  const message = environment === 'test'
                    ? 'Only Testers may manage the QA test emails.'
                    : 'Only Developers may manage the QA test emails.';
                  
                  setSnackbar({
                    open: true,
                    message: message,
                    severity: 'info'
                  });
                }
              }}
              fullWidth
              sx={{
                backgroundColor: '#2563eb',
                color: 'white',
                borderRadius: '6px',
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: 'Poppins, sans-serif',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#1d4ed8'
                }
              }}
            >
              QA Test Email Management
            </Button>
            <Button
              variant="contained"
              onClick={() => setBugsOpen(true)}
              fullWidth
              sx={{
                backgroundColor: '#2563eb',
                color: 'white',
                borderRadius: '6px',
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: 'Poppins, sans-serif',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#1d4ed8'
                }
              }}
            >
              View Bugs from Google Sheets
            </Button>
            <Button
              variant="contained"
              onClick={() => setTaskWorkflowOpen(true)}
              disabled={!isDeveloper && !isQATester}
              fullWidth
              sx={{
                backgroundColor: (isDeveloper || isQATester) ? '#2563eb' : '#9ca3af',
                color: 'white',
                borderRadius: '6px',
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: 'Poppins, sans-serif',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: (isDeveloper || isQATester) ? '#1d4ed8' : '#9ca3af'
                },
                cursor: (isDeveloper || isQATester) ? 'pointer' : 'not-allowed'
              }}
              title={(!isDeveloper && !isQATester) ? 'Developer, QA, or Tester role required to access workflow tasks' : ''}
            >
              Task Workflow
            </Button>
          </Box>
        </Box>

      </Box>

      {/* Bulk Import Dialog */}
      <Dialog open={bulkImportOpen} onClose={() => !importing && setBulkImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
          Bulk Import Shops from Google Places
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#6b7280' }}>
            Enter a zipcode to import all car repair shops from Google Places API for that area.
            This will create shops in the database that can be used for testing.
          </Typography>
          <TextField
            fullWidth
            label="ZIP Code"
            value={zipcode}
            onChange={(e) => setZipcode(e.target.value.replace(/\D/g, '').slice(0, 5))}
            placeholder="76022"
            disabled={importing}
            sx={{ mb: 2 }}
            helperText="Enter a 5-digit ZIP code"
          />
          {importResult && (
            <Alert severity={importResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
              {importResult.success ? (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Import Complete!
                  </Typography>
                  <Typography variant="body2">
                    Created: {importResult.summary?.created || 0} shops<br/>
                    Updated: {importResult.summary?.updated || 0} shops<br/>
                    Skipped: {importResult.summary?.skipped || 0} shops
                  </Typography>
                </>
              ) : (
                importResult.error || 'Import failed'
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {importResult?.success ? (
            <>
              <Button
                onClick={() => {
                  setBulkImportOpen(false);
                  setZipcode('');
                  setImportResult(null);
                }}
                variant="outlined"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setZipcode('');
                  setImportResult(null);
                }}
                variant="contained"
                sx={{
                  backgroundColor: '#4285f4',
                  '&:hover': { backgroundColor: '#1b449e' }
                }}
              >
                Import Another
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => {
                setBulkImportOpen(false);
                setZipcode('');
                setImportResult(null);
              }} disabled={importing}>
                Cancel
              </Button>
              <Button
                onClick={handleBulkImport}
                variant="contained"
                disabled={!zipcode || zipcode.length !== 5 || importing}
                sx={{
                  backgroundColor: '#4285f4',
                  '&:hover': { backgroundColor: '#1b449e' }
                }}
              >
                {importing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Import Shops'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Refresh Reviews Dialog */}
      <Dialog 
        open={refreshReviewsOpen} 
        onClose={() => !refreshing && setRefreshReviewsOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
          Refresh Google Reviews
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#6b7280' }}>
            Manually trigger Google review population for shops that have a Place ID but are missing reviews.
            This is useful for backfilling reviews after fixing issues.
          </Typography>
          <TextField
            fullWidth
            label="Number of Days"
            type="number"
            value={refreshDays}
            onChange={(e) => setRefreshDays(e.target.value)}
            placeholder="7"
            disabled={refreshing}
            sx={{ mb: 2 }}
            helperText="Refresh reviews for shops created in the last N days"
            inputProps={{ min: 1, max: 365 }}
          />
          {refreshResult && (
            <Alert severity={refreshResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
              {refreshResult.success ? (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Refresh Complete!
                  </Typography>
                  <Typography variant="body2">
                    Total shops: {refreshResult.summary?.total || 0}<br/>
                    Success: {refreshResult.summary?.success || 0}<br/>
                    Failed: {refreshResult.summary?.failed || 0}
                  </Typography>
                  {refreshResult.errors && refreshResult.errors.length > 0 && (
                    <Typography variant="body2" sx={{ mt: 1, fontSize: '12px' }}>
                      <strong>Errors:</strong><br/>
                      {refreshResult.errors.slice(0, 5).map((err, idx) => (
                        <span key={idx}>• {err}<br/></span>
                      ))}
                      {refreshResult.errors.length > 5 && (
                        <span>...and {refreshResult.errors.length - 5} more</span>
                      )}
                    </Typography>
                  )}
                </>
              ) : (
                refreshResult.error || 'Refresh failed'
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {refreshResult?.success ? (
            <>
              <Button
                onClick={() => {
                  setRefreshReviewsOpen(false);
                  setRefreshDays('7');
                  setRefreshResult(null);
                }}
                variant="outlined"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setRefreshDays('7');
                  setRefreshResult(null);
                }}
                variant="contained"
                sx={{
                  backgroundColor: '#10b981',
                  '&:hover': { backgroundColor: '#059669' }
                }}
              >
                Refresh Again
              </Button>
            </>
          ) : (
            <>
              <Button 
                onClick={() => {
                  setRefreshReviewsOpen(false);
                  setRefreshDays('7');
                  setRefreshResult(null);
                }} 
                disabled={refreshing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRefreshReviews}
                variant="contained"
                disabled={!refreshDays || parseInt(refreshDays) < 1 || refreshing}
                sx={{
                  backgroundColor: '#10b981',
                  '&:hover': { backgroundColor: '#059669' }
                }}
              >
                {refreshing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Refresh Reviews'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* QA Test Email Management Dialog */}
      <Dialog 
        open={qaEmailManagementOpen} 
        onClose={() => {
          setQaEmailManagementOpen(false);
          setManagedEmails([]);
        }}
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
          QA Test Email Management
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: '#6b7280' }}>
            Manage and reset test accounts (qa.*@test.autoshop.com). This will safely delete test data 
            and all associated dependencies while preventing orphaned records.
          </Typography>
          
          {/* Your Managed Emails - Quick Reset Section */}
          {(environment === 'test' || environment === 'dev' || environment === 'staging' || environment === 'preprod') && userEmail && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#ecfdf5', borderRadius: 1, border: '1px solid #86efac' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#065f46' }}>
                Your Managed Emails
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#047857', fontSize: '0.875rem' }}>
                Emails you can reset. Click the reset button to quickly reset any of these accounts.
              </Typography>
              {loadingManagedEmails ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" sx={{ color: '#047857' }}>Loading your managed emails...</Typography>
                </Box>
              ) : managedEmails.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {managedEmails.map((email) => (
                    <Box 
                      key={email} 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        p: 1.5,
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '1px solid #d1fae5'
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#065f46' }}>
                        {email}
                      </Typography>
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        onClick={() => handleResetTestAccounts([email])}
                        disabled={resetting}
                        sx={{
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontFamily: 'Poppins, sans-serif',
                          minWidth: '100px'
                        }}
                      >
                        {resetting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Reset'}
                      </Button>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: '#047857', fontStyle: 'italic' }}>
                  No managed emails found. You can only reset your own email or emails you've been granted admin access for.
                </Typography>
              )}
            </Box>
          )}
          
          {/* Custom Email Reset (Test Environment Only) */}
          {environment === 'test' && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#fef3c7', borderRadius: 1, border: '1px solid #fbbf24' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#92400e' }}>
                Reset Custom Email (Test Environment)
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#78350f', fontSize: '0.875rem' }}>
                Enter a specific email address to reset. <strong>You can reset your own email address</strong> (must match your logged-in email exactly) 
                or emails you are admin for. The system will automatically detect if the email is a User, Mechanic, or Shop Owner 
                and reset all related data accordingly. If the email is a Shop Owner, shops will be reset to unclaimed status.
              </Typography>
              <TextField
                fullWidth
                label="Email Address to Reset"
                placeholder="e.g., Azaus26@yahoo.com"
                value={customEmailsToReset}
                onChange={(e) => setCustomEmailsToReset(e.target.value)}
                sx={{ mb: 2 }}
                helperText="Enter one email address. The system will detect the role and reset all related data."
              />
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  const email = customEmailsToReset.trim().toLowerCase();
                  if (!email) {
                    setSnackbar({
                      open: true,
                      message: 'Please enter an email address',
                      severity: 'error'
                    });
                    return;
                  }
                  
                  // Validate email format
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(email)) {
                    setSnackbar({
                      open: true,
                      message: 'Please enter a valid email address',
                      severity: 'error'
                    });
                    return;
                  }
                  
                  handleResetTestAccounts([email]);
                }}
                disabled={resetting || !customEmailsToReset.trim()}
                sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontFamily: 'Poppins, sans-serif'
                }}
              >
                {resetting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Reset This Email'}
              </Button>
            </Box>
          )}
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Test Account Groups
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
              All test accounts matching the pattern: <strong>qa.*@test.autoshop.com</strong>
            </Typography>
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f0f9ff', borderRadius: 1, border: '1px solid #bae6fd' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: '#0369a1' }}>
                💡 Email Group Patterns:
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#075985', fontSize: '0.75rem' }}>
                Test accounts can be organized by email pattern (e.g., "qa.tester1.%@test.autoshop.com", "qa.tester2.%@test.autoshop.com"). 
                Use "Reset Selected" to reset specific accounts or groups.
              </Typography>
            </Box>
            
            {!dependencies && (
              <Button
                variant="outlined"
                onClick={handleCheckDependencies}
                disabled={checkingDependencies}
                sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontFamily: 'Poppins, sans-serif',
                  mb: 2
                }}
              >
                {checkingDependencies ? <CircularProgress size={20} /> : '🔍 Check Dependencies'}
              </Button>
            )}

            {dependencies && (
              <>
                <Box sx={{ mb: 2, p: 2, bgcolor: '#f9fafb', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ mb: 2, color: '#dc2626', fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
                    Total items to be deleted/reset: {
                      (dependencies.dependencies.users || 0) + 
                      (dependencies.dependencies.owners || 0) + 
                      (dependencies.dependencies.mechanics || 0) +
                      (dependencies.dependencies.shops || 0) +
                      (dependencies.dependencies.cars || 0) +
                      (dependencies.dependencies.serviceRequests || 0) +
                      (dependencies.dependencies.carHistory || 0) +
                      (dependencies.dependencies.bids || 0) +
                      (dependencies.dependencies.subscriptions || 0) +
                      (dependencies.dependencies.messages || 0) +
                      (dependencies.dependencies.invites || 0)
                    }
                  </Typography>
                  <Typography component="div" variant="body2" sx={{ color: '#374151', fontFamily: 'Poppins, sans-serif', fontSize: '14px' }}>
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '20px' }}>
                      <li style={{ marginBottom: '4px' }}>
                        Test Accounts: {
                          (dependencies.dependencies.users || 0) + 
                          (dependencies.dependencies.owners || 0) + 
                          (dependencies.dependencies.mechanics || 0)
                        }
                      </li>
                      <li style={{ marginBottom: '4px' }}>
                        Admins: {dependencies.dependencies.adminUsers || 0}
                      </li>
                      <li style={{ marginBottom: '4px' }}>
                        Users: {dependencies.dependencies.regularUsers || 0}
                      </li>
                      <li style={{ marginBottom: '4px' }}>
                        Mechanics: {
                          (dependencies.dependencies.owners || 0) + 
                          (dependencies.dependencies.mechanics || 0)
                        } ({dependencies.dependencies.owners || 0} Owners + {dependencies.dependencies.mechanics || 0} Mechanic{dependencies.dependencies.mechanics !== 1 ? 's' : ''})
                      </li>
                      <li style={{ marginBottom: '4px' }}>
                        Shops: {dependencies.dependencies.shops || 0}
                      </li>
                      <li style={{ marginBottom: '4px' }}>
                        Cars: {dependencies.dependencies.cars || 0}
                      </li>
                      <li>
                        Other: {
                          (dependencies.dependencies.serviceRequests || 0) +
                          (dependencies.dependencies.carHistory || 0) +
                          (dependencies.dependencies.bids || 0) +
                          (dependencies.dependencies.subscriptions || 0) +
                          (dependencies.dependencies.messages || 0) +
                          (dependencies.dependencies.invites || 0)
                        }
                      </li>
                    </Box>
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleResetTestAccounts()}
                    disabled={resetting}
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontFamily: 'Poppins, sans-serif',
                      flex: 1
                    }}
                  >
                    {resetting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Reset All Test Accounts'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setSelectedEmails(new Set());
                      setSelectionModalOpen(true);
                    }}
                    disabled={resetting}
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontFamily: 'Poppins, sans-serif',
                      flex: 1
                    }}
                  >
                    Reset Selected
                  </Button>
                </Box>
              </>
            )}
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Safe Deletion Process:</strong> Click "Check Dependencies" first to see what will be deleted. 
              The system will safely delete all test data and reset shops to unclaimed status, preventing orphaned records.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          {dependencies && (
            <Button 
              onClick={() => {
                setDependencies(null);
                setSelectedEmails(new Set());
                setQaEmailManagementOpen(false);
              }}
              sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none' }}
            >
              Close
            </Button>
          )}
          {!dependencies && (
            <Button 
              onClick={() => setQaEmailManagementOpen(false)}
              sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none' }}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Account Selection Modal */}
      <Dialog
        open={selectionModalOpen}
        onClose={() => {
          setSelectionModalOpen(false);
          setSelectedEmails(new Set());
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
          Select Accounts to Reset
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Select the accounts you want to reset:
            </Typography>
            <Box>
              <Button
                size="small"
                onClick={() => {
                  const allEmails = [
                    ...(dependencies?.testAccounts?.users || []).map(u => u.email),
                    ...(dependencies?.testAccounts?.owners || []).map(o => o.email),
                    ...(dependencies?.testAccounts?.mechanics || []).map(m => m.email)
                  ];
                  setSelectedEmails(new Set(allEmails));
                }}
                sx={{ mr: 1, textTransform: 'none', fontSize: '0.75rem' }}
              >
                Select All
              </Button>
              <Button
                size="small"
                onClick={() => setSelectedEmails(new Set())}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                Clear All
              </Button>
            </Box>
          </Box>

          <Box sx={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 1, p: 1.5 }}>
            {/* Test Users */}
            {dependencies?.testAccounts?.users && dependencies.testAccounts.users.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', display: 'block', mb: 1 }}>
                  Users ({dependencies.testAccounts.users.length})
                </Typography>
                {dependencies.testAccounts.users.map((user) => (
                  <FormControlLabel
                    key={user.email}
                    control={
                      <Checkbox
                        checked={selectedEmails.has(user.email)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedEmails);
                          if (e.target.checked) {
                            newSelected.add(user.email);
                          } else {
                            newSelected.delete(user.email);
                          }
                          setSelectedEmails(newSelected);
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {user.email}
                      </Typography>
                    }
                    sx={{ display: 'flex', mb: 0.5, alignItems: 'center' }}
                  />
                ))}
              </Box>
            )}

            {/* Owners */}
            {dependencies?.testAccounts?.owners && dependencies.testAccounts.owners.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', display: 'block', mb: 1 }}>
                  Owners ({dependencies.testAccounts.owners.length})
                </Typography>
                {dependencies.testAccounts.owners.map((owner) => (
                  <FormControlLabel
                    key={owner.email}
                    control={
                      <Checkbox
                        checked={selectedEmails.has(owner.email)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedEmails);
                          if (e.target.checked) {
                            newSelected.add(owner.email);
                          } else {
                            newSelected.delete(owner.email);
                          }
                          setSelectedEmails(newSelected);
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {owner.email}
                        </Typography>
                        <Chip label="Owner" size="small" sx={{ height: '18px', fontSize: '0.7rem' }} />
                      </Box>
                    }
                    sx={{ display: 'flex', mb: 0.5, alignItems: 'center' }}
                  />
                ))}
              </Box>
            )}

            {/* Mechanics */}
            {dependencies?.testAccounts?.mechanics && dependencies.testAccounts.mechanics.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', display: 'block', mb: 1 }}>
                  Mechanics ({dependencies.testAccounts.mechanics.length})
                </Typography>
                {dependencies.testAccounts.mechanics.map((mechanic) => (
                  <FormControlLabel
                    key={mechanic.email}
                    control={
                      <Checkbox
                        checked={selectedEmails.has(mechanic.email)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedEmails);
                          if (e.target.checked) {
                            newSelected.add(mechanic.email);
                          } else {
                            newSelected.delete(mechanic.email);
                          }
                          setSelectedEmails(newSelected);
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {mechanic.email}
                        </Typography>
                        <Chip label="Mechanic" size="small" sx={{ height: '18px', fontSize: '0.7rem' }} />
                      </Box>
                    }
                    sx={{ display: 'flex', mb: 0.5, alignItems: 'center' }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSelectionModalOpen(false);
              setSelectedEmails(new Set());
            }}
            sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              const emails = Array.from(selectedEmails);
              setSelectionModalOpen(false);
              await handleResetTestAccounts(emails);
              setSelectedEmails(new Set());
            }}
            disabled={resetting || selectedEmails.size === 0}
            sx={{
              fontFamily: 'Poppins, sans-serif',
              textTransform: 'none',
              borderRadius: '6px'
            }}
          >
            {resetting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : `Reset (${selectedEmails.size})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Token Expiration Countdown Modal */}
      {/* Session expiration modal removed - too intrusive */}
      {/* <CountdownModal /> */}

      {/* Bugs from Google Sheets Dialog */}
      <Dialog 
        open={bugsOpen} 
        onClose={() => setBugsOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Bugs from Google Sheets</span>
          <Button
            size="small"
            onClick={() => setShowConfig(!showConfig)}
            sx={{
              fontFamily: 'Poppins, sans-serif',
              textTransform: 'none',
              fontSize: '12px'
            }}
          >
            {showConfig ? 'Hide' : '⚙️'} Configuration
          </Button>
        </DialogTitle>
        <DialogContent>
          {showConfig && (
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Configure Google Sheet Connection
              </Typography>
              <TextField
                fullWidth
                label="Default Spreadsheet ID"
                value={bugsSpreadsheetId}
                onChange={async (e) => {
                  let newId = e.target.value.trim();
                  
                  // Extract Spreadsheet ID from full Google Sheets URL if provided
                  // Pattern: https://docs.google.com/spreadsheets/d/{ID}/edit...
                  // Works for both: full URL or just the ID
                  if (newId.includes('docs.google.com/spreadsheets/d/')) {
                    const match = newId.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
                    if (match && match[1]) {
                      newId = match[1];
                      console.log('✅ Extracted Spreadsheet ID from URL:', newId);
                    } else {
                      // If URL format detected but extraction failed, show error
                      console.warn('⚠️ Could not extract Spreadsheet ID from URL');
                    }
                  }
                  // If it's just the ID (no URL), use it as-is - no extraction needed
                  
                  setBugsSpreadsheetId(newId);
                  // Save to localStorage
                  if (newId) {
                    localStorage.setItem('qa_bugs_spreadsheet_id', newId);
                    
                    // Load saved sheets and sheet name for this spreadsheet ID
                    // Then validate/refresh from API to catch any changes
                    try {
                      const savedSheets = localStorage.getItem(`qa_bugs_sheets_${newId}`);
                      if (savedSheets) {
                        const sheets = JSON.parse(savedSheets);
                        setAvailableSheets(sheets); // Show saved sheets immediately
                        
                        const savedSheetName = localStorage.getItem(`qa_bugs_sheet_name_${newId}`);
                        if (savedSheetName && sheets.includes(savedSheetName)) {
                          setBugsSheetName(savedSheetName);
                        } else if (sheets.length > 0) {
                          setBugsSheetName(sheets[0]);
                          localStorage.setItem(`qa_bugs_sheet_name_${newId}`, sheets[0]);
                        }
                      }
                    } catch (e) {
                      // If no saved data, reset
                      setAvailableSheets([]);
                      setBugsSheetName('Sheet1');
                    }
                    
                    // Always refresh from API to get latest sheet names (in background)
                    if (newId && newId.length > 10) {
                      setLoadingMetadata(true);
                      fetch(`${getApiBase()}/api/qa/bugs/metadata?spreadsheetId=${encodeURIComponent(newId)}`, { credentials: 'include' })
                        .then(res => res.json())
                        .then(data => {
                          if (data.metadata?.sheets) {
                            const sheetNames = data.metadata.sheets
                              .map((s) => s.title)
                              .filter(title => title.toLowerCase() !== 'home');
                            setAvailableSheets(sheetNames);
                            localStorage.setItem(`qa_bugs_sheets_${newId}`, JSON.stringify(sheetNames));
                            
                            // Validate current sheet name still exists
                            const currentSheetName = bugsSheetName;
                            if (currentSheetName && !sheetNames.includes(currentSheetName)) {
                              // Saved sheet no longer exists, use first available
                              if (sheetNames.length > 0) {
                                setBugsSheetName(sheetNames[0]);
                                localStorage.setItem(`qa_bugs_sheet_name_${newId}`, sheetNames[0]);
                              }
                            } else if (!currentSheetName && sheetNames.length > 0) {
                              // No current sheet, use first available
                              setBugsSheetName(sheetNames[0]);
                              localStorage.setItem(`qa_bugs_sheet_name_${newId}`, sheetNames[0]);
                            }
                          }
                        })
                        .catch(error => {
                          console.error('Error refreshing sheet metadata:', error);
                          // Don't show error - keep using saved data
                        })
                        .finally(() => {
                          setLoadingMetadata(false);
                        });
                    }
                  } else {
                    localStorage.removeItem('qa_bugs_spreadsheet_id');
                    setAvailableSheets([]);
                    setBugsSheetName('Sheet1');
                  }
                  
                  setBugs([]);
                  setBugsError('');
                  
                  // Auto-fetch available tabs when spreadsheet ID is entered (if not already saved)
                  if (newId && newId.length > 10) {
                    // Check if we have saved sheets for this ID
                    const savedSheets = localStorage.getItem(`qa_bugs_sheets_${newId}`);
                    if (!savedSheets) {
                      // No saved sheets, fetch them
                      setLoadingMetadata(true);
                      try {
                        const response = await fetch(
                          `${getApiBase()}/api/qa/bugs/metadata?spreadsheetId=${encodeURIComponent(newId)}`,
                          { credentials: 'include' }
                        );
                        const data = await response.json();
                        if (response.ok && data.metadata?.sheets) {
                          const sheetNames = data.metadata.sheets
                            .map((s) => s.title)
                            .filter(title => title.toLowerCase() !== 'home');
                          setAvailableSheets(sheetNames);
                          // Save to localStorage keyed by spreadsheet ID
                          localStorage.setItem(`qa_bugs_sheets_${newId}`, JSON.stringify(sheetNames));
                          
                          // Load saved sheet name for this spreadsheet, or use first available
                          const savedSheetName = localStorage.getItem(`qa_bugs_sheet_name_${newId}`);
                          if (savedSheetName && sheetNames.includes(savedSheetName)) {
                            setBugsSheetName(savedSheetName);
                          } else if (sheetNames.length > 0) {
                            const newSheetName = sheetNames[0];
                            setBugsSheetName(newSheetName);
                            localStorage.setItem(`qa_bugs_sheet_name_${newId}`, newSheetName);
                          }
                        }
                      } catch (error) {
                        console.error('Error fetching sheet metadata:', error);
                        // Don't show error - user might not have entered valid ID yet
                      } finally {
                        setLoadingMetadata(false);
                      }
                    }
                  }
                }}
                placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                sx={{ mb: 2 }}
                helperText="Get this from the Google Sheets URL. This will be saved for future use."
              />
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                💡 Tip: Once configured, this spreadsheet ID will be remembered for next time.
              </Typography>
            </Box>
          )}
          
          {!showConfig && bugsSpreadsheetId && (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                Connected to: {bugsSpreadsheetId.substring(0, 20)}...
              </Typography>
              <Button
                size="small"
                onClick={() => setShowConfig(true)}
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  textTransform: 'none',
                  fontSize: '12px',
                  padding: '2px 8px'
                }}
              >
                Change Sheet
              </Button>
            </Box>
          )}
          
          {!showConfig && !bugsSpreadsheetId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Click "⚙️ Configuration" above to set up your Google Sheet connection.
            </Alert>
          )}
          
          <Box sx={{ mb: 3 }}>
            {bugsSpreadsheetId && (
              <>
                {loadingMetadata && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Loading available tabs...
                    </Typography>
                  </Box>
                )}
                {availableSheets.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Sheet Tab</InputLabel>
                      <Select
                        value={bugsSheetName}
                        label="Sheet Tab"
                        onChange={(e) => {
                          const newSheetName = e.target.value;
                          setBugsSheetName(newSheetName);
                          setBugs([]); // Clear bugs when switching tabs
                          // Save selected sheet name to localStorage
                          if (bugsSpreadsheetId) {
                            localStorage.setItem(`qa_bugs_sheet_name_${bugsSpreadsheetId}`, newSheetName);
                          }
                        }}
                      >
                        {availableSheets.map((sheetName) => (
                          <MenuItem key={sheetName} value={sheetName}>
                            {sheetName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      onClick={async () => {
                        setLoadingMetadata(true);
                        try {
                          const response = await fetch(
                            `${getApiBase()}/api/qa/bugs/metadata?spreadsheetId=${encodeURIComponent(bugsSpreadsheetId)}`,
                            { credentials: 'include' }
                          );
                          const data = await response.json();
                          if (response.ok && data.metadata?.sheets) {
                            const sheetNames = data.metadata.sheets
                              .map((s) => s.title)
                              .filter(title => title.toLowerCase() !== 'home');
                            setAvailableSheets(sheetNames);
                            localStorage.setItem(`qa_bugs_sheets_${bugsSpreadsheetId}`, JSON.stringify(sheetNames));
                            
                            // Validate current sheet name still exists
                            const currentSheetName = bugsSheetName;
                            if (currentSheetName && !sheetNames.includes(currentSheetName)) {
                              // Current sheet no longer exists, use first available
                              if (sheetNames.length > 0) {
                                setBugsSheetName(sheetNames[0]);
                                localStorage.setItem(`qa_bugs_sheet_name_${bugsSpreadsheetId}`, sheetNames[0]);
                                setBugs([]); // Clear bugs since sheet changed
                              }
                            }
                          }
                        } catch (error) {
                          console.error('Error refreshing sheet metadata:', error);
                          setBugsError('Failed to refresh sheet tabs');
                        } finally {
                          setLoadingMetadata(false);
                        }
                      }}
                      variant="outlined"
                      size="small"
                      disabled={loadingMetadata || !bugsSpreadsheetId}
                      sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none', minWidth: 'auto', alignSelf: 'flex-end' }}
                      title="Refresh sheet list to see any changes"
                    >
                      Refresh
                    </Button>
                  </Box>
                ) : !loadingMetadata && (
                  <TextField
                    fullWidth
                    label="Sheet Name"
                    value={bugsSheetName}
                    onChange={(e) => {
                    const newSheetName = e.target.value;
                    setBugsSheetName(newSheetName);
                    // Save selected sheet name to localStorage
                    if (bugsSpreadsheetId) {
                      localStorage.setItem(`qa_bugs_sheet_name_${bugsSpreadsheetId}`, newSheetName);
                    }
                  }}
                    placeholder="Sheet1"
                    sx={{ mb: 2 }}
                    helperText="Name of the sheet tab"
                  />
                )}
              </>
            )}
            {bugsSpreadsheetId && (
              <Button
                variant="contained"
                onClick={async () => {
                  if (!bugsSpreadsheetId) {
                    setBugsError('Please enter a Spreadsheet ID');
                    return;
                  }
                  setBugsLoading(true);
                  setBugsError('');
                  try {
                    const response = await fetch(
                      `${getApiBase()}/api/qa/bugs?spreadsheetId=${encodeURIComponent(bugsSpreadsheetId)}&sheetName=${encodeURIComponent(bugsSheetName)}`,
                      { credentials: 'include' }
                    );
                    const data = await response.json();
                    if (response.ok) {
                      const bugsList = data.bugs || [];
                      setBugs(bugsList);
                      
                      // Refresh bug count from ALL tabs
                      if (bugsSpreadsheetId) {
                        try {
                          const env = environment || 'dev';
                          const countResponse = await fetch(
                            `${getApiBase()}/api/qa/bugs/count?spreadsheetId=${encodeURIComponent(bugsSpreadsheetId)}&environment=${encodeURIComponent(env)}`,
                            { credentials: 'include' }
                          );
                          if (countResponse.ok) {
                            const countData = await countResponse.json();
                            setBugCount(countData.count || 0);
                            if (countData.label) {
                              setBugCountLabel(countData.label);
                            }
                          }
                        } catch (error) {
                          console.error('Error refreshing bug count:', error);
                        }
                      }
                    } else {
                      setBugsError(data.error || data.message || 'Failed to fetch bugs');
                    }
                  } catch (error) {
                    setBugsError('Error fetching bugs: ' + error.message);
                  } finally {
                    setBugsLoading(false);
                  }
                }}
                disabled={bugsLoading || !bugsSpreadsheetId}
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  textTransform: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#2563eb',
                  '&:hover': {
                    backgroundColor: '#1d4ed8'
                  }
                }}
              >
                {bugsLoading ? 'Loading...' : 'Load Bugs'}
              </Button>
            )}
          </Box>

          {bugsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bugsError}
            </Alert>
          )}

          {bugs.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Found {bugs.length} bugs
              </Typography>
              {statusUpdateSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setStatusUpdateSuccess('')}>
                  {statusUpdateSuccess}
                </Alert>
              )}
              <Box sx={{ maxHeight: '500px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f3f4f6', position: 'sticky', top: 0 }}>
                      {Object.keys(bugs[0]).map((key) => (
                        <th key={key} style={{ padding: '8px', textAlign: 'left', border: '1px solid #e5e7eb' }}>
                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bugs.map((bug, index) => {
                      // Get the actual ID value (may be empty string)
                      const rawBugId = bug.id || bug.bug_id || bug['#'];
                      // Get title for fallback when ID is empty
                      const titleKey = Object.keys(bug).find(k => 
                        k.toLowerCase().includes('title')
                      ) || 'title';
                      const bugTitle = bug[titleKey] || '';
                      
                      // Use ID if available, otherwise use title (never use row number)
                      const bugId = rawBugId && rawBugId.toString().trim() !== '' 
                        ? rawBugId 
                        : (bugTitle || `Row ${index + 2}`);
                      
                      // Determine search column: use 'ID' if we have an ID, otherwise 'Title'
                      const searchColumn = rawBugId && rawBugId.toString().trim() !== '' 
                        ? 'ID' 
                        : 'Title';
                      
                      const statusKey = Object.keys(bug).find(k => 
                        k.toLowerCase().includes('status')
                      ) || 'status';
                      const isUpdating = updatingStatus[bugId];
                      
                      return (
                        <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          {Object.keys(bugs[0]).map((key) => {
                            // Make status column editable
                            if (key.toLowerCase().includes('status')) {
                              const currentStatus = bug[key] || '';
                              // Get allowed transitions based on current status, environment, and role
                              const allowedTransitions = getAllowedStatusTransitions(currentStatus);
                              // Include current status in options so it's visible, plus allowed transitions
                              const statusOptions = [currentStatus, ...allowedTransitions].filter(Boolean);
                              
                              return (
                                <td key={key} style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                                  {isUpdating ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Select
                                      value={currentStatus}
                                      onChange={async (e) => {
                                        const newStatus = e.target.value;
                                        setUpdatingStatus({ ...updatingStatus, [bugId]: true });
                                        setStatusUpdateSuccess('');
                                        
                                        try {
                                          const response = await fetch(
                                            `${getApiBase()}/api/qa/bugs/update-status`,
                                            {
                                              method: 'PUT',
                                              headers: {
                                                'Content-Type': 'application/json',
                                              },
                                              credentials: 'include',
                                              body: JSON.stringify({
                                                spreadsheetId: bugsSpreadsheetId,
                                                sheetName: bugsSheetName,
                                                bugIdentifier: bugId, // ID or Title
                                                statusColumn: 'Status', // Status column name
                                                newStatus: newStatus,
                                                searchColumn: searchColumn // Search in ID or Title column
                                              }),
                                            }
                                          );
                                          
                                          const data = await response.json();
                                          
                                          if (response.ok && data.success) {
                                            // Update local state
                                            const updatedBugs = bugs.map((b, i) => {
                                              if (i === index) {
                                                return { ...b, [key]: newStatus };
                                              }
                                              return b;
                                            });
                                            setBugs(updatedBugs);
                                            setStatusUpdateSuccess(`Status updated to "${newStatus}" for bug ${bugId}`);
                                            setTimeout(() => setStatusUpdateSuccess(''), 3000);
                                            
                                            // Refresh bug count from ALL tabs
                                            if (bugsSpreadsheetId) {
                                              try {
                                                const env = environment || 'dev';
                                                const countResponse = await fetch(
                                                  `${getApiBase()}/api/qa/bugs/count?spreadsheetId=${encodeURIComponent(bugsSpreadsheetId)}&environment=${encodeURIComponent(env)}`,
                                                  { credentials: 'include' }
                                                );
                                                if (countResponse.ok) {
                                                  const countData = await countResponse.json();
                                                  setBugCount(countData.count || 0);
                                                  if (countData.label) {
                                                    setBugCountLabel(countData.label);
                                                  }
                                                }
                                              } catch (error) {
                                                console.error('Error refreshing bug count:', error);
                                              }
                                            }
                                          } else {
                                            setBugsError(data.error || 'Failed to update status');
                                          }
                                        } catch (error) {
                                          console.error('Error updating status:', error);
                                          setBugsError('Failed to update status. Please try again.');
                                        } finally {
                                          setUpdatingStatus({ ...updatingStatus, [bugId]: false });
                                        }
                                      }}
                                      size="small"
                                      sx={{
                                        minWidth: 120,
                                        fontSize: '0.875rem',
                                        '& .MuiSelect-select': {
                                          padding: '4px 8px',
                                        }
                                      }}
                                    >
                                      {statusOptions.map((status) => (
                                        <MenuItem key={status} value={status}>
                                          {status}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  )}
                                </td>
                              );
                            }
                            
                            // Regular cell
                            return (
                              <td key={key} style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                                {bug[key] || '-'}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}

          {bugs.length === 0 && !bugsLoading && !bugsError && bugsSpreadsheetId && (
            <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 4 }}>
              {availableSheets.length > 0 
                ? `Select a tab above and click "Load Bugs" to fetch bugs from Google Sheets`
                : 'Click "Load Bugs" to fetch bugs from Google Sheets'}
            </Typography>
          )}
          
          {!bugsSpreadsheetId && !showConfig && (
            <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 4 }}>
              Configure your Google Sheet connection to get started
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBugsOpen(false);
              setBugs([]);
              setBugsError('');
              // Don't clear spreadsheet ID - keep it for next time
              setBugsSheetName('Sheet1');
              setAvailableSheets([]);
              setShowConfig(false);
            }}
            sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Workflow Dialog */}
      <Dialog 
        open={taskWorkflowOpen} 
        onClose={() => setTaskWorkflowOpen(false)}
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
            Task Workflow
          </Typography>
          <Button
            data-refresh-tasks
            onClick={async () => {
              if (!bugsSpreadsheetId) {
                setTasksError('Please configure Google Sheet connection first');
                return;
              }
              // Prevent rapid clicks (rate limiting)
              if (tasksLoading) return;
              
              setTasksLoading(true);
              setTasksError('');
              try {
                const response = await fetch(
                  `${getApiBase()}/api/qa/bugs/tasks?spreadsheetId=${encodeURIComponent(bugsSpreadsheetId)}&sheetName=${encodeURIComponent(bugsSheetName)}`,
                  { credentials: 'include' }
                );
                const data = await response.json();
                
                // Handle quota exceeded error
                if (response.status === 429 || (data.error && data.error.includes('quota'))) {
                  setTasksError('Google Sheets API quota exceeded. Please wait 60 seconds before refreshing again.');
                  return;
                }
                
                if (response.ok) {
                  const tasksList = data.tasks || [];
                  // Filter tasks based on environment and role
                  const filteredTasks = filterTasksByEnvironmentAndRole(tasksList);
                  setTasks(filteredTasks);
                  
                  // Refresh bug count from ALL tabs
                  if (bugsSpreadsheetId) {
                    try {
                      const env = environment || 'dev';
                      const countResponse = await fetch(
                        `${getApiBase()}/api/qa/bugs/count?spreadsheetId=${encodeURIComponent(bugsSpreadsheetId)}&environment=${encodeURIComponent(env)}`,
                        { credentials: 'include' }
                      );
                      if (countResponse.ok) {
                        const countData = await countResponse.json();
                        setBugCount(countData.count || 0);
                        if (countData.label) {
                          setBugCountLabel(countData.label);
                        }
                      }
                    } catch (error) {
                      console.error('Error refreshing bug count:', error);
                    }
                  }
                  
                  // Reset to first task, or keep current if still valid
                  if (currentTaskIndex >= filteredTasks.length || filteredTasks.length === 0) {
                    setCurrentTaskIndex(0);
                  }
                } else {
                  // Provide more helpful error message based on context
                  if (data.error && data.error.includes('No tasks')) {
                    if (environment === 'dev' && isDeveloper) {
                      setTasksError('No tasks available for Developers in DEV environment. Tasks must be in status: New, In Progress, Fixed (Dev), Failed in Test, or Hold.');
                    } else if (environment === 'test' && isQATester) {
                      setTasksError('No tasks available for QA/Testers in TEST environment. Tasks must be in status: Deployed to Test, Deployed to Prod, or Hold.');
                    } else if (environment === 'test' && isDeveloper) {
                      setTasksError('No tasks available for Developers in TEST environment. Tasks must be in status: Verified in Test or Hold.');
                    } else if ((environment === 'preprod' || environment === 'staging') && (isDeveloper || isQATester)) {
                      setTasksError('No tasks available in PREPROD/STAGING environment. Tasks must be in status: Deployed to Staging, Verified in Staging, Ready for Production, Deployed to Prod, or Hold.');
                    } else {
                      setTasksError('No tasks available for your role in this environment.');
                    }
                  } else {
                    setTasksError(data.error || 'Failed to load tasks');
                  }
                }
              } catch (error) {
                console.error('Error loading tasks:', error);
                if (environment === 'dev' && isDeveloper) {
                  setTasksError('Failed to load tasks. No tasks available for Developers in DEV environment (New, In Progress, Fixed (Dev), Failed in Test, or Hold).');
                } else if (environment === 'test' && isQATester) {
                  setTasksError('Failed to load tasks. No tasks available for QA/Testers in TEST environment (Deployed to Test, Deployed to Prod, or Hold).');
                } else if (environment === 'test' && isDeveloper) {
                  setTasksError('Failed to load tasks. No tasks available for Developers in TEST environment (Verified in Test or Hold).');
                } else if ((environment === 'preprod' || environment === 'staging') && (isDeveloper || isQATester)) {
                  setTasksError('Failed to load tasks. No tasks available in PREPROD/STAGING environment (Deployed to Staging, Verified in Staging, Ready for Production, Deployed to Prod, or Hold).');
                } else {
                  setTasksError('Failed to load tasks. Please check your Google Sheet connection.');
                }
              } finally {
                setTasksLoading(false);
              }
            }}
            variant="outlined"
            size="small"
            disabled={tasksLoading || !bugsSpreadsheetId}
            sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none' }}
            title="Reload bug/task data from the selected sheet tab"
          >
            {tasksLoading ? 'Loading...' : 'Refresh Tasks'}
          </Button>
        </DialogTitle>
        <DialogContent>
          {/* Environment and Role Info */}
          <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            icon={false}
          >
            <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, mb: 0.5 }}>
              Environment: <span style={{ textTransform: 'uppercase' }}>{environment}</span> | 
              Role: {isDeveloper ? 'Developer' : isQATester ? 'QA/Tester' : 'Viewer'}
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: 'Poppins, sans-serif', color: '#6b7280' }}>
              {environment === 'dev' && isDeveloper && 'You can see: New, In Progress, Fixed (Dev), Failed in Test, Hold'}
              {environment === 'test' && isQATester && 'You can see: Deployed to Test, Deployed to Prod, Hold'}
              {environment === 'test' && isDeveloper && 'You can see: Verified in Test, Hold'}
              {(environment === 'preprod' || environment === 'staging') && (isDeveloper || isQATester || isAdmin) && 'You can see: Deployed to Staging, Verified in Staging, Ready for Production, Deployed to Prod, Hold'}
            </Typography>
          </Alert>
          
          {(!isDeveloper && !isQATester) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                You are viewing in read-only mode. Developer or QA/Tester role required to edit workflow tasks.
              </Typography>
            </Alert>
          )}
          {tasksError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {tasksError}
            </Alert>
          )}

          {!bugsSpreadsheetId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Please configure your Google Sheet connection in "View Bugs from Google Sheets" first.
            </Alert>
          )}

          {bugsSpreadsheetId && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                {availableSheets.length === 0 ? (
                  <Button
                    onClick={async () => {
                      setLoadingMetadata(true);
                      try {
                        const response = await fetch(
                          `${getApiBase()}/api/qa/bugs/metadata?spreadsheetId=${encodeURIComponent(bugsSpreadsheetId)}`,
                          { credentials: 'include' }
                        );
                        const data = await response.json();
                        if (response.ok && data.metadata?.sheets) {
                          const sheetNames = data.metadata.sheets
                            .map((s) => s.title)
                            .filter(title => title.toLowerCase() !== 'home');
                          setAvailableSheets(sheetNames);
                          localStorage.setItem(`qa_bugs_sheets_${bugsSpreadsheetId}`, JSON.stringify(sheetNames));
                          
                          // Load saved sheet name or use first available
                          const savedSheetName = localStorage.getItem(`qa_bugs_sheet_name_${bugsSpreadsheetId}`);
                          if (savedSheetName && sheetNames.includes(savedSheetName)) {
                            setBugsSheetName(savedSheetName);
                          } else if (sheetNames.length > 0) {
                            setBugsSheetName(sheetNames[0]);
                            localStorage.setItem(`qa_bugs_sheet_name_${bugsSpreadsheetId}`, sheetNames[0]);
                          }
                        }
                      } catch (error) {
                        console.error('Error fetching sheet metadata:', error);
                        setTasksError('Failed to load sheet tabs');
                      } finally {
                        setLoadingMetadata(false);
                      }
                    }}
                    variant="outlined"
                    size="small"
                    disabled={loadingMetadata}
                    sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none' }}
                  >
                    {loadingMetadata ? 'Loading...' : 'Load Sheet Tabs'}
                  </Button>
                ) : (
                  <>
                    <FormControl fullWidth size="small" sx={{ flex: 1 }}>
                      <InputLabel>Sheet Tab</InputLabel>
                      <Select
                        value={bugsSheetName}
                        label="Sheet Tab"
                        onChange={(e) => {
                          const newSheetName = e.target.value;
                          setBugsSheetName(newSheetName);
                          setTasks([]); // Clear tasks when switching tabs
                          setCurrentTaskIndex(0);
                          // Save selected sheet name to localStorage
                          localStorage.setItem(`qa_bugs_sheet_name_${bugsSpreadsheetId}`, newSheetName);
                        }}
                      >
                        {availableSheets.map((sheet) => (
                          <MenuItem key={sheet} value={sheet}>
                            {sheet}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      onClick={async () => {
                        // Prevent rapid clicks
                        if (loadingMetadata) return;
                        
                        setLoadingMetadata(true);
                        try {
                          const response = await fetch(
                            `${getApiBase()}/api/qa/bugs/metadata?spreadsheetId=${encodeURIComponent(bugsSpreadsheetId)}`,
                            { credentials: 'include' }
                          );
                          const data = await response.json();
                          
                          // Handle quota exceeded error
                          if (response.status === 429 || (data.error && data.error.includes('quota'))) {
                            setTasksError('Google Sheets API quota exceeded. Please wait 60 seconds before refreshing again.');
                            return;
                          }
                          
                          if (response.ok && data.metadata?.sheets) {
                            const sheetNames = data.metadata.sheets
                              .map((s) => s.title)
                              .filter(title => title.toLowerCase() !== 'home');
                            setAvailableSheets(sheetNames);
                            localStorage.setItem(`qa_bugs_sheets_${bugsSpreadsheetId}`, JSON.stringify(sheetNames));
                            
                            // Validate current sheet name still exists
                            const currentSheetName = bugsSheetName;
                            if (currentSheetName && !sheetNames.includes(currentSheetName)) {
                              // Current sheet no longer exists, use first available
                              if (sheetNames.length > 0) {
                                setBugsSheetName(sheetNames[0]);
                                localStorage.setItem(`qa_bugs_sheet_name_${bugsSpreadsheetId}`, sheetNames[0]);
                                setTasks([]); // Clear tasks since sheet changed
                                setCurrentTaskIndex(0);
                              }
                            }
                          }
                        } catch (error) {
                          console.error('Error refreshing sheet metadata:', error);
                          setTasksError('Failed to refresh sheet tabs');
                        } finally {
                          setLoadingMetadata(false);
                        }
                      }}
                      variant="outlined"
                      size="small"
                      disabled={loadingMetadata}
                      sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none', minWidth: 'auto' }}
                      title="Refresh the list of available sheet tabs"
                    >
                      Refresh Tabs
                    </Button>
                  </>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                Currently using: <strong>{bugsSheetName}</strong>
                {availableSheets.length > 0 && ` (${availableSheets.length} tabs available)`}
              </Typography>
            </Box>
          )}

          {tasks.length === 0 && !tasksLoading && bugsSpreadsheetId && !tasksError && (
            <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 4 }}>
              {environment === 'dev' && isDeveloper && 'No tasks available for Developers in DEV environment. Tasks must be in status: New, In Progress, Fixed (Dev), Failed in Test, or Hold.'}
              {environment === 'test' && isQATester && 'No tasks available for QA/Testers in TEST environment. Tasks must be in status: Deployed to Test, Deployed to Prod, or Hold.'}
              {environment === 'test' && isDeveloper && 'No tasks available for Developers in TEST environment. Tasks must be in status: Verified in Test or Hold.'}
              {(environment === 'preprod' || environment === 'staging') && (isDeveloper || isQATester || isAdmin) && 'No tasks available in PREPROD/STAGING environment. Tasks must be in status: Deployed to Staging, Verified in Staging, Ready for Production, Deployed to Prod, or Hold.'}
              {(!isDeveloper && !isQATester) && 'No active tasks found. All bugs may be Closed, or the sheet may be empty.'}
            </Typography>
          )}

          {tasks.length > 0 && (
            <Box>
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f3f4f6', borderRadius: '8px' }}>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                  Progress: {currentTaskIndex + 1} of {tasks.length} tasks
                </Typography>
                <Box sx={{ width: '100%', height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                  <Box 
                    sx={{ 
                      width: `${((currentTaskIndex + 1) / tasks.length) * 100}%`, 
                      height: '100%', 
                      backgroundColor: '#2563eb',
                      transition: 'width 0.3s ease'
                    }} 
                  />
                </Box>
              </Box>

              {tasks[currentTaskIndex] && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Current Task #{currentTaskIndex + 1}
                  </Typography>
                  
                  <Box sx={{ p: 2, border: '2px solid #8b5cf6', borderRadius: '8px', backgroundColor: '#faf5ff' }}>
                    {Object.keys(tasks[currentTaskIndex]).map((key) => {
                      const value = tasks[currentTaskIndex][key];
                      if (!value || value.toString().trim() === '') return null;
                      
                      // Special handling for status column
                      if (key.toLowerCase().includes('status')) {
                        const currentStatus = value.toString();
                        // Get allowed transitions based on current status, environment, and role
                        const allowedTransitions = getAllowedStatusTransitions(currentStatus);
                        // Always include current status in the list
                        const statusOptions = allowedTransitions.length > 0 
                          ? [currentStatus, ...allowedTransitions.filter(s => s !== currentStatus)]
                          : [currentStatus];
                        
                        // Determine if user can edit (must have appropriate role for environment)
                        const canEdit = (environment === 'dev' && isDeveloper) || 
                                       (environment === 'test' && (isQATester || isDeveloper)) ||
                                       ((environment === 'preprod' || environment === 'staging') && (isDeveloper || isQATester || isAdmin));
                        
                        return (
                          <Box key={key} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#6b7280' }}>
                              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                            </Typography>
                            <Select
                              value={currentStatus}
                              disabled={!canEdit || (statusOptions.length === 1 && statusOptions[0] === currentStatus)}
                              onChange={async (e) => {
                                const newStatus = e.target.value;
                                // Get the actual ID value (may be empty string)
                                const rawBugId = tasks[currentTaskIndex].id || tasks[currentTaskIndex].bug_id || tasks[currentTaskIndex]['#'];
                                // Get title for fallback when ID is empty
                                const titleKey = Object.keys(tasks[currentTaskIndex] || {}).find(k => 
                                  k.toLowerCase().includes('title')
                                ) || 'title';
                                const bugTitle = tasks[currentTaskIndex][titleKey] || '';
                                
                                // Use ID if available, otherwise use title (never use row number)
                                const bugId = rawBugId && rawBugId.toString().trim() !== '' 
                                  ? rawBugId 
                                  : (bugTitle || `Row ${currentTaskIndex + 2}`);
                                
                                // Determine search column: use 'ID' if we have an ID, otherwise 'Title'
                                const searchColumn = rawBugId && rawBugId.toString().trim() !== '' 
                                  ? 'ID' 
                                  : 'Title';
                                
                                try {
                                  const response = await fetch(
                                    `${getApiBase()}/api/qa/bugs/update-status`,
                                    {
                                      method: 'PUT',
                                      headers: {
                                        'Content-Type': 'application/json',
                                      },
                                      credentials: 'include',
                                      body: JSON.stringify({
                                        spreadsheetId: bugsSpreadsheetId,
                                        sheetName: bugsSheetName,
                                        bugIdentifier: bugId, // ID or Title
                                        statusColumn: 'Status', // Status column name
                                        newStatus: newStatus,
                                        searchColumn: searchColumn // Search in ID or Title column
                                      }),
                                    }
                                  );
                                  
                                  const data = await response.json();
                                  
                                  if (response.ok && data.success) {
                                    // Refresh bug count from ALL tabs
                                    if (bugsSpreadsheetId) {
                                      try {
                                        const env = environment || 'dev';
                                        const countResponse = await fetch(
                                          `${getApiBase()}/api/qa/bugs/count?spreadsheetId=${encodeURIComponent(bugsSpreadsheetId)}&environment=${encodeURIComponent(env)}`,
                                          { credentials: 'include' }
                                        );
                                        if (countResponse.ok) {
                                          const countData = await countResponse.json();
                                          setBugCount(countData.count || 0);
                                          if (countData.label) {
                                            setBugCountLabel(countData.label);
                                          }
                                        }
                                      } catch (error) {
                                        console.error('Error refreshing bug count:', error);
                                      }
                                    }
                                    
                                    // Check if task should disappear after status change
                                    const taskWillDisappear = 
                                      (environment === 'dev' && newStatus === 'Deployed to Test') ||
                                      (environment === 'test' && (newStatus === 'Deployed to Staging' || newStatus === 'Closed'));
                                    
                                    if (taskWillDisappear) {
                                      // Task will be filtered out, so refresh the entire list
                                      setTimeout(async () => {
                                        try {
                                          const refreshResponse = await fetch(
                                            `${getApiBase()}/api/qa/bugs/tasks?spreadsheetId=${encodeURIComponent(bugsSpreadsheetId)}&sheetName=${encodeURIComponent(bugsSheetName)}`,
                                            { credentials: 'include' }
                                          );
                                          const refreshData = await refreshResponse.json();
                                          if (refreshResponse.ok) {
                                            const tasksList = refreshData.tasks || [];
                                            const filteredTasks = filterTasksByEnvironmentAndRole(tasksList);
                                            setTasks(filteredTasks);
                                            
                                            // Adjust current index
                                            if (currentTaskIndex >= filteredTasks.length) {
                                              setCurrentTaskIndex(Math.max(0, filteredTasks.length - 1));
                                            }
                                          }
                                        } catch (error) {
                                          console.error('Error refreshing tasks after status change:', error);
                                        }
                                      }, 500);
                                    } else {
                                      // Update local state (task still visible)
                                      const updatedTasks = [...tasks];
                                      updatedTasks[currentTaskIndex] = { ...updatedTasks[currentTaskIndex], [key]: newStatus };
                                      setTasks(updatedTasks);
                                      
                                      // Auto-advance for convenience
                                      if (
                                        (environment === 'dev' && newStatus === 'Fixed (Dev)') ||
                                        (environment === 'test' && (newStatus === 'Deployed to Staging' || newStatus === 'Deployed to Prod'))
                                      ) {
                                        if (currentTaskIndex < tasks.length - 1) {
                                          setTimeout(() => {
                                            setCurrentTaskIndex(currentTaskIndex + 1);
                                          }, 500);
                                        }
                                      }
                                    }
                                  } else {
                                    setTasksError(data.error || 'Failed to update status');
                                  }
                                } catch (error) {
                                  console.error('Error updating status:', error);
                                  setTasksError('Failed to update status');
                                }
                              }}
                              size="small"
                              fullWidth
                              sx={{
                                backgroundColor: 'white',
                                '& .MuiSelect-select': {
                                  padding: '8px 12px',
                                }
                              }}
                            >
                              {statusOptions.map((status) => (
                                <MenuItem 
                                  key={status} 
                                  value={status}
                                >
                                  {status}
                                  {status === currentStatus && ' (current)'}
                                </MenuItem>
                              ))}
                              {statusOptions.length === 1 && statusOptions[0] === currentStatus && (
                                <MenuItem disabled>
                                  <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#6b7280' }}>
                                    No transitions available for this status in {environment} environment
                                  </Typography>
                                </MenuItem>
                              )}
                            </Select>
                          </Box>
                        );
                      }
                      
                      return (
                        <Box key={key} sx={{ mb: 1.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#6b7280' }}>
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#111827' }}>
                            {value.toString()}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Screenshots Section */}
                  {tasks[currentTaskIndex] && tasks[currentTaskIndex].id && bugsSpreadsheetId && (() => {
                    const currentTaskId = tasks[currentTaskIndex].id || tasks[currentTaskIndex].bug_id || tasks[currentTaskIndex]['#'] || currentTaskIndex + 1;
                    const rowNumber = parseInt(currentTaskId) + 1;
                    // Format: Use range parameter to scroll to specific cell
                    // Try using both gid and range - Google Sheets should scroll to the range
                    const linkUrl = bugsSheetGid 
                      ? `https://docs.google.com/spreadsheets/d/${bugsSpreadsheetId}/edit?gid=${bugsSheetGid}#range=L${rowNumber}`
                      : `https://docs.google.com/spreadsheets/d/${bugsSpreadsheetId}/edit`;
                    
                    return (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontFamily: 'Poppins, sans-serif', color: '#1f2937' }}>
                          Screenshots:
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          href={linkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontFamily: 'Poppins, sans-serif',
                            textTransform: 'none',
                            borderColor: '#2563eb',
                            color: '#2563eb',
                            fontSize: '13px',
                            '&:hover': {
                              borderColor: '#1d4ed8',
                              backgroundColor: '#eff6ff'
                            }
                          }}
                        >
                          📸 View Screenshots in Google Sheet
                        </Button>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#6b7280', fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                          {bugsSheetGid 
                            ? `Opens "${bugsSheetName}" tab at cell L${rowNumber}`
                            : `Opens Google Sheet (navigate to "${bugsSheetName}" tab, row ${rowNumber}, column L)`
                          }
                        </Typography>
                      </Box>
                    );
                  })()}

                  {/* Dev Comment Section */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontFamily: 'Poppins, sans-serif', color: '#1f2937' }}>
                      Dev Comment:
                    </Typography>
                    <TextField
                      multiline
                      rows={3}
                      fullWidth
                      value={editingDevComment}
                      onChange={(e) => setEditingDevComment(e.target.value)}
                      disabled={!isDeveloper || savingDevComment}
                      placeholder={isDeveloper ? "Add developer comments here..." : "No developer comments"}
                      sx={{ 
                        fontFamily: 'Poppins, sans-serif',
                        '& .MuiInputBase-input': {
                          fontFamily: 'Poppins, sans-serif',
                          fontSize: '14px'
                        },
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-disabled': {
                            backgroundColor: '#f9fafb'
                          }
                        }
                      }}
                    />
                    
                    {/* Save/Reset Buttons - Only show for developers */}
                    {isDeveloper && (() => {
                      const task = tasks[currentTaskIndex];
                      const devCommentKey = Object.keys(task).find(k => 
                        k.toLowerCase().replace(/\s+/g, '_') === 'dev_comment' || 
                        k.toLowerCase() === 'dev comment'
                      );
                      const currentValue = devCommentKey ? (task[devCommentKey] || '') : '';
                      const hasChanges = editingDevComment !== currentValue;

                      return (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                          <Button
                            onClick={() => {
                              setEditingDevComment(currentValue);
                            }}
                            disabled={savingDevComment || !hasChanges}
                            size="small"
                            sx={{ 
                              fontFamily: 'Poppins, sans-serif', 
                              textTransform: 'none',
                              color: '#6b7280',
                              fontSize: '13px',
                              '&:hover': {
                                backgroundColor: '#f3f4f6'
                              }
                            }}
                          >
                            Reset
                          </Button>
                          <Button
                            variant="contained"
                            onClick={async () => {
                              // Validation
                              if (!bugsSpreadsheetId || !bugsSheetName) {
                                setTasksError('Missing spreadsheet configuration');
                                return;
                              }

                              // Get the actual ID value (may be empty string)
                              const rawBugId = task.id || task.bug_id || task['#'];
                              // Get title for fallback when ID is empty
                              const titleKey = Object.keys(task || {}).find(k => 
                                k.toLowerCase().includes('title')
                              ) || 'title';
                              const bugTitle = task[titleKey] || '';
                              
                              // Use ID if available, otherwise use title (never use row number)
                              const bugId = rawBugId && rawBugId.toString().trim() !== '' 
                                ? rawBugId 
                                : (bugTitle || `Row ${currentTaskIndex + 2}`);
                              
                              // Determine search column: use 'ID' if we have an ID, otherwise 'Title'
                              const searchColumn = rawBugId && rawBugId.toString().trim() !== '' 
                                ? 'ID' 
                                : 'Title';
                              
                              if (!bugId) {
                                setTasksError('Missing bug ID or title');
                                return;
                              }

                              setSavingDevComment(true);
                              setTasksError('');

                              try {
                                const response = await fetch(`${getApiBase()}/api/qa/bugs/update-dev-comment`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  credentials: 'include',
                                  body: JSON.stringify({
                                    spreadsheetId: bugsSpreadsheetId,
                                    sheetName: bugsSheetName,
                                    bugIdentifier: bugId,
                                    devComment: editingDevComment,
                                    searchColumn: searchColumn // Search in ID or Title column
                                  })
                                });

                                const data = await response.json();

                                if (response.ok && data.success) {
                                  // Update local task data
                                  const updatedTasks = [...tasks];
                                  if (devCommentKey) {
                                    updatedTasks[currentTaskIndex] = {
                                      ...updatedTasks[currentTaskIndex],
                                      [devCommentKey]: editingDevComment
                                    };
                                  } else {
                                    // If column doesn't exist in local data, add it
                                    updatedTasks[currentTaskIndex] = {
                                      ...updatedTasks[currentTaskIndex],
                                      'Dev Comment': editingDevComment
                                    };
                                  }
                                  setTasks(updatedTasks);
                                  
                                  // Show success message
                                  setStatusUpdateSuccess('Dev comment saved successfully!');
                                  setTimeout(() => setStatusUpdateSuccess(''), 3000);
                                  
                                  console.log('✅ Dev comment saved');
                                } else {
                                  setTasksError(data.error || 'Failed to save dev comment');
                                }
                              } catch (error) {
                                console.error('❌ Error saving dev comment:', error);
                                setTasksError('Error saving dev comment: ' + error.message);
                              } finally {
                                setSavingDevComment(false);
                              }
                            }}
                            disabled={savingDevComment || !hasChanges}
                            size="small"
                            sx={{ 
                              fontFamily: 'Poppins, sans-serif', 
                              textTransform: 'none',
                              fontSize: '13px',
                              backgroundColor: '#2563eb',
                              '&:hover': {
                                backgroundColor: '#1d4ed8'
                              },
                              '&:disabled': {
                                backgroundColor: '#9ca3af',
                                color: '#ffffff'
                              }
                            }}
                          >
                            {savingDevComment ? 'Saving...' : 'Save Comment'}
                          </Button>
                        </Box>
                      );
                    })()}
                  </Box>

                  <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {(() => {
                      // Get current status
                      const statusKey = Object.keys(tasks[currentTaskIndex]).find(k => k.toLowerCase().includes('status'));
                      if (!statusKey) return null;
                      
                      const currentStatus = (tasks[currentTaskIndex][statusKey]?.toString().trim() || '').trim();
                      const allowedTransitions = getAllowedStatusTransitions(currentStatus);
                      
                      // Filter out current status - only show transitions that are NOT the current status
                      // Use case-insensitive comparison to handle any formatting differences
                      const availableTransitions = allowedTransitions.filter(status => 
                        status.trim().toLowerCase() !== currentStatus.toLowerCase()
                      );
                      
                      // Determine if user can edit
                      const canEdit = (environment === 'dev' && isDeveloper) || 
                                     (environment === 'test' && (isQATester || isDeveloper)) ||
                                     ((environment === 'preprod' || environment === 'staging') && (isDeveloper || isQATester || isAdmin));
                      
                      if (!canEdit || availableTransitions.length === 0) {
                        return null;
                      }
                      
                      // Helper function to update status
                      const handleStatusUpdate = async (newStatus, autoAdvance = false) => {
                        // Get the actual ID value (may be empty string)
                        const rawBugId = tasks[currentTaskIndex].id || tasks[currentTaskIndex].bug_id || tasks[currentTaskIndex]['#'];
                        // Get title for fallback when ID is empty
                        const titleKey = Object.keys(tasks[currentTaskIndex] || {}).find(k => 
                          k.toLowerCase().includes('title')
                        ) || 'title';
                        const bugTitle = tasks[currentTaskIndex][titleKey] || '';
                        
                        // Use ID if available, otherwise use title (never use row number)
                        const bugId = rawBugId && rawBugId.toString().trim() !== '' 
                          ? rawBugId 
                          : (bugTitle || `Row ${currentTaskIndex + 2}`);
                        
                        // Determine search column: use 'ID' if we have an ID, otherwise 'Title'
                        const searchColumn = rawBugId && rawBugId.toString().trim() !== '' 
                          ? 'ID' 
                          : 'Title';
                        
                        try {
                          const response = await fetch(
                            `${getApiBase()}/api/qa/bugs/update-status`,
                            {
                              method: 'PUT',
                              headers: {
                                'Content-Type': 'application/json',
                              },
                              credentials: 'include',
                              body: JSON.stringify({
                                spreadsheetId: bugsSpreadsheetId,
                                sheetName: bugsSheetName,
                                bugIdentifier: bugId,
                                statusColumn: 'Status',
                                newStatus: newStatus,
                                searchColumn: searchColumn // Search in ID or Title column
                              }),
                            }
                          );
                          
                          const data = await response.json();
                          
                          if (response.ok && data.success) {
                            // Refresh bug count from ALL tabs
                            if (bugsSpreadsheetId) {
                              try {
                                const env = environment || 'dev';
                                const countResponse = await fetch(
                                  `${getApiBase()}/api/qa/bugs/count?spreadsheetId=${encodeURIComponent(bugsSpreadsheetId)}&environment=${encodeURIComponent(env)}`,
                                  { credentials: 'include' }
                                );
                                if (countResponse.ok) {
                                  const countData = await countResponse.json();
                                  setBugCount(countData.count || 0);
                                  if (countData.label) {
                                    setBugCountLabel(countData.label);
                                  }
                                }
                              } catch (error) {
                                console.error('Error refreshing bug count:', error);
                              }
                            }
                            
                            // Check if task should disappear after status change
                            const taskWillDisappear = 
                              (environment === 'dev' && newStatus === 'Deployed to Test') ||
                              (environment === 'test' && (newStatus === 'Deployed to Staging' || newStatus === 'Closed'));
                            
                            if (taskWillDisappear) {
                              // Task will be filtered out, so refresh the entire list
                              setTimeout(async () => {
                                try {
                                  const refreshResponse = await fetch(
                                    `${getApiBase()}/api/qa/bugs/tasks?spreadsheetId=${encodeURIComponent(bugsSpreadsheetId)}&sheetName=${encodeURIComponent(bugsSheetName)}`,
                                    { credentials: 'include' }
                                  );
                                  const refreshData = await refreshResponse.json();
                                  if (refreshResponse.ok) {
                                    const tasksList = refreshData.tasks || [];
                                    const filteredTasks = filterTasksByEnvironmentAndRole(tasksList);
                                    setTasks(filteredTasks);
                                    
                                    // Adjust current index
                                    if (currentTaskIndex >= filteredTasks.length) {
                                      setCurrentTaskIndex(Math.max(0, filteredTasks.length - 1));
                                    }
                                  }
                                } catch (error) {
                                  console.error('Error refreshing tasks after status change:', error);
                                }
                              }, 500);
                            } else {
                              // Update local state (task still visible)
                              const updatedTasks = [...tasks];
                              updatedTasks[currentTaskIndex] = { ...updatedTasks[currentTaskIndex], [statusKey]: newStatus };
                              setTasks(updatedTasks);
                              
                              // Auto-advance if requested
                              if (autoAdvance && currentTaskIndex < tasks.length - 1) {
                                setTimeout(() => {
                                  setCurrentTaskIndex(currentTaskIndex + 1);
                                }, 500);
                              }
                            }
                          } else {
                            setTasksError(data.error || 'Failed to update status');
                          }
                        } catch (error) {
                          console.error('Error updating status:', error);
                          setTasksError('Failed to update status');
                        }
                      };
                      
                      // Status button styling map
                      const getStatusButtonStyle = (status) => {
                        const isPrimary = status === 'Fixed (Dev)' || status === 'Verified in Test' || status === 'Deployed to Prod';
                        return {
                          backgroundColor: isPrimary ? '#2563eb' : '#374151',
                          fontFamily: 'Poppins, sans-serif',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: isPrimary ? '#1d4ed8' : '#1f2937'
                          }
                        };
                      };
                      
                      // Get button label
                      const getStatusButtonLabel = (status) => {
                        if (status === 'Fixed (Dev)') return 'Fixed in Dev & Next';
                        return status;
                      };
                      
                      return availableTransitions.map((status) => (
                        <Button
                          key={status}
                          onClick={async () => {
                            const shouldAutoAdvance = status === 'Fixed (Dev)' || status === 'Deployed to Prod';
                            await handleStatusUpdate(status, shouldAutoAdvance);
                          }}
                          variant="contained"
                          sx={getStatusButtonStyle(status)}
                        >
                          {getStatusButtonLabel(status)}
                        </Button>
                      ));
                    })()}
                    
                    {currentTaskIndex < tasks.length - 1 && (
                      <Button
                        onClick={() => setCurrentTaskIndex(currentTaskIndex + 1)}
                        variant="outlined"
                        sx={{
                          fontFamily: 'Poppins, sans-serif',
                          textTransform: 'none',
                        }}
                      >
                        Skip to Next →
                      </Button>
                    )}
                    
                    {currentTaskIndex > 0 && (
                      <Button
                        onClick={() => setCurrentTaskIndex(currentTaskIndex - 1)}
                        variant="outlined"
                        sx={{
                          fontFamily: 'Poppins, sans-serif',
                          textTransform: 'none',
                        }}
                      >
                        ← Previous
                      </Button>
                    )}
                  </Box>
                  
                  {/* Message when on last task - suggest checking other sheets */}
                  {tasks.length > 0 && currentTaskIndex === tasks.length - 1 && availableSheets.length > 1 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        📋 You've completed all tasks in the <strong>{bugsSheetName}</strong> sheet.
                        {availableSheets.filter(s => s !== bugsSheetName).length > 0 && (
                          <>
                            {' '}There {availableSheets.filter(s => s !== bugsSheetName).length === 1 ? 'is' : 'are'} {availableSheets.filter(s => s !== bugsSheetName).length} other sheet{availableSheets.filter(s => s !== bugsSheetName).length === 1 ? '' : 's'} available: <strong>{availableSheets.filter(s => s !== bugsSheetName).join(', ')}</strong>
                          </>
                        )}
                        {' '}Switch to another sheet tab above to see if there are more tasks available.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTaskWorkflowOpen(false);
              setTasks([]);
              setCurrentTaskIndex(0);
              setTasksError('');
            }}
            sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bug Burndown Chart Modal */}
      <Dialog 
        open={burndownModalOpen} 
        onClose={() => setBurndownModalOpen(false)}
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
            Bug Burndown
          </Typography>
          <Button
            onClick={() => fetchBurndownData()}
            variant="outlined"
            size="small"
            disabled={burndownLoading}
            sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none' }}
          >
            Refresh
          </Button>
        </DialogTitle>
        <DialogContent>
          {burndownLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <CircularProgress />
            </Box>
          ) : burndownData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                {bugsSpreadsheetId ? 'No bug data available. Make sure your Google Sheet is configured correctly.' : 'Please configure your Google Sheet connection to view burndown data.'}
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Current Counts Summary */}
              {burndownData.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                    <CardContent>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        Development
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#4285f4', mt: 1 }}>
                        {burndownData[burndownData.length - 1]?.issues || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                    <CardContent>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        Testing
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#fbbc04', mt: 1 }}>
                        {burndownData[burndownData.length - 1]?.testing || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                    <CardContent>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        Staging
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#6366f1', mt: 1 }}>
                        {burndownData[burndownData.length - 1]?.staging || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                    <CardContent>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        Total Active
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#4285f4', mt: 1 }}>
                        {burndownData[burndownData.length - 1]?.total || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Detailed Burndown Chart */}
              <Box sx={{ height: '500px', mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={burndownData} animationDuration={2000}>
                    <XAxis 
                      dataKey="displayDate" 
                      tick={{ fontSize: 11 }}
                      stroke="#9ca3af"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      stroke="#9ca3af"
                      label={{ value: 'Bug Count', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}
                      formatter={(value, name) => {
                        const labels = {
                          issues: 'Development',
                          testing: 'Testing',
                          staging: 'Staging',
                          total: 'Total'
                        };
                        return [`${value}`, labels[name] || name];
                      }}
                      labelFormatter={(label) => {
                        const dataPoint = burndownData.find(d => d.displayDate === label);
                        return dataPoint ? `${dataPoint.dayOfWeek}, ${label}` : label;
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
                      iconType="line"
                    />
                    <Line
                      type="monotone"
                      dataKey="issues"
                      stroke="#4285f4"
                      strokeWidth={3}
                      dot={false}
                      activeDot={{ r: 6 }}
                      name="Development"
                    />
                    <Line
                      type="monotone"
                      dataKey="testing"
                      stroke="#fbbc04"
                      strokeWidth={3}
                      dot={false}
                      activeDot={{ r: 6 }}
                      name="Testing"
                    />
                    <Line
                      type="monotone"
                      dataKey="staging"
                      stroke="#6366f1"
                      strokeWidth={3}
                      dot={false}
                      activeDot={{ r: 6 }}
                      name="Staging"
                    />
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke="#4285f4"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      activeDot={{ r: 5 }}
                      name="Total"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBurndownModalOpen(false)}
            sx={{ fontFamily: 'Poppins, sans-serif', textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MasterAdminDashboard;

